<template>
	<div>
		<v-btn
			v-bind="btnProps"
			v-test="'project-share'"
			v-track="'project-share-button'"
			icon="icon-share-contact-stroke"
			aria-label="Share"
			elevation="0"
			variant="plain"
			:disabled="isDisabled"
			:retain-focus-on-click="false"
			@click.stop="init"></v-btn>

		<!-- LSP-17773: custom prototype-matched Share dialog (design-ref/live-proto.js + lp-real.css).
		     Teleported so it escapes the trigger's stacking context and matches the prototype's fixed overlay. -->
		<Teleport to="body">
			<div v-if="dialog" class="rs-root" v-test="'project-share-dialog'">
				<div class="rs-dim" data-close @click="close"></div>

				<!-- ======================= MANAGE ACCESS VIEW ======================= -->
				<div
					v-if="manageView"
					ref="dialogPanel"
					class="rs-dlg"
					role="dialog"
					aria-modal="true"
					aria-label="People with access to project"
					tabindex="-1">
					<div class="rs-h manage">
						<button class="rs-mback" type="button" v-test="'project-share-dialog-manage-back'" @click="manageView = false">
							<span v-html="icons.chevL"></span> Back
						</button>
						<h2>People With Access To Project</h2>
					</div>
					<p v-if="project?.name" v-test="'project-share-dialog-project-name'" class="text-body-2 text-break font-weight-medium px-4 pt-3 mb-0">
						{{ project.name }}
					</p>
					<div class="rs-search">
						<span v-html="icons.search"></span>
						<input
							ref="customerSearch"
							v-model="sharedUsersSearchText"
							v-test="'project-share-dialog-people-search'"
							type="text"
							placeholder="Search people with access"
							:aria-invalid="searchFieldProps(sharedUsersSearchText)['aria-invalid']"
							:disabled="peopleLoading || sharedUsers.length === 0" />
					</div>
					<p v-if="searchFieldProps(sharedUsersSearchText).counter" class="text-caption text-error">
						{{ searchFieldProps(sharedUsersSearchText).counterValue() }}/{{ searchFieldProps(sharedUsersSearchText).counter }}
						{{ searchFieldProps(sharedUsersSearchText).errorMessages[0] }}
					</p>
					<p
						v-if="derivedAccessSummaryLoading"
						v-test="'project-share-dialog-derived-access-loading'"
						class="text-body-2 text-medium-emphasis px-4 py-3 mb-0"
						role="status"
						aria-busy="true">
						Loading organization access details…
					</p>
					<p
						v-else-if="derivedAccessSummaryText"
						v-test="'project-share-dialog-derived-access'"
						class="text-body-2 text-medium-emphasis px-4 py-3 mb-0">
						{{ derivedAccessSummaryText }}
					</p>
					<div v-if="!peopleLoading" data-plist>
						<div v-if="roleMenuFor" class="rs-menucatch" @click="closeRoleMenu"></div>
						<div v-if="paginatedUsers.length === 0 && sharedUsersSearchText" class="rs-empty">No people match that search.</div>
						<div v-for="item in paginatedUsers" :key="item.id" class="rs-row" :data-pid="item.id">
							<div class="rs-meta">
								<div class="rs-nm">{{ getTitle(item) }}</div>
								<div class="rs-sub2">{{ getSubtitle(item) }}</div>
								<!-- LSP-17773: pending/claimed, bounced, expired, trial-ended, guest markers. -->
								<div v-if="rowBadges(item).length" class="rs-badges" v-test="'project-share-dialog-row-badges'">
									<span v-for="badge in rowBadges(item)" :key="badge.text" class="rs-badge" :data-tone="badge.tone">{{
										badge.text
									}}</span>
								</div>
								<button
									v-if="canResendInvite(item)"
									class="rs-resend"
									type="button"
									v-test="'project-share-dialog-resend'"
									:disabled="isResendBlocked(item)"
									@click="resendInvite(item)">
									{{ resendLabel(item) }}
								</button>
							</div>
							<button
								class="rs-pill"
								:class="{ dis: isAccessTypeDisabled(item) }"
								type="button"
								v-test="'project-share-dialog-role-pill'"
								@click="toggleRoleMenu(item, $event)">
								{{ personRoleLabel(item) }} <span v-if="!isAccessTypeDisabled(item)" v-html="icons.chevD"></span>
							</button>
							<div v-if="roleMenuFor === item.id" class="r-menu rs-rolemenu" :class="{ 'menu-up': roleMenuOpensUpward }">
								<button
									v-for="option in roleMenuOptions"
									:key="option.value"
									class="opt"
									type="button"
									:data-selected="item.accessType === option.value"
									@click="chooseRole(item, option.value)">
									<span v-html="icons.check"></span>{{ option.label }}
								</button>
								<div v-if="item.userIdentifierType === 'phone'" class="note">
									Invited by phone — their link is tied to their number. They'll add an email when they first open the
									project.
								</div>
								<button class="opt danger" type="button" @click="chooseRole(item, REMOVE_ACCESS.value)">
									<span class="check" style="opacity: 0"></span>Remove access
								</button>
							</div>
						</div>
						<v-pagination v-model="currentPage" :length="pageCount" :total-visible="0" size="small" class="mt-2" />
						<div v-if="sharedUsersPageLabel" v-test="'project-share-dialog-page-counter'" class="rs-pagelabel">
							{{ sharedUsersPageLabel }}
						</div>
					</div>
					<div v-else>
						<v-skeleton-loader type="list-item-two-line@5"></v-skeleton-loader>
					</div>
					<div class="rs-foot">
						<span></span>
						<button class="rs-shareb" type="button" v-test="'project-share-dialog-manage-back2'" @click="manageView = false">
							Back
						</button>
					</div>
				</div>

				<!-- ======================= MAIN SHARE VIEW ======================= -->
				<div v-else ref="dialogPanel" class="rs-dlg share" role="dialog" aria-modal="true" aria-label="Share project" tabindex="-1">
					<div class="rs-h">
						<h2>Share With Others</h2>
						<div class="rs-hactions">
							<button
								class="rs-linkbtn"
								type="button"
								title="Copy link"
								:disabled="!project"
								v-test="'project-share-dialog-copy-link'"
								@click="copyLink"
								v-html="icons.link"></button>
							<button
								class="rs-closex"
								type="button"
								title="Close"
								aria-label="Close"
								data-close
								@click="close"
								v-html="icons.x"></button>
						</div>
					</div>

					<p v-if="project?.name" v-test="'project-share-dialog-project-name'" class="text-body-2 text-break font-weight-medium px-4 pt-3 mb-0">
						{{ project.name }}
					</p>
					<ShareInviteField
						ref="inviteField"
						:directory="memberItems"
						:staged="staged"
						:used-identifiers="usedIdentifiers"
						:team-emails="teamEmails"
						:my-domain="myDomain"
						:default-country="orgDefaultCountry"
						:searching="directorySearching"
						:search-failed="directorySearchFailed"
						:min-search-length="minDirectorySearchLength"
						:can-manage-team="canManageTeam"
						@stage="stageEntry"
						@query-change="handleInviteQueryChange"
						@create-contact="openCreateContact"
						@add-to-team="onAddToTeam" />

					<div v-if="staged.length" class="rs-stagesec" v-test="'project-share-dialog-staged'">
						<div class="rs-stagehead">Ready to share ({{ staged.length }})</div>
						<ShareStagedRow
							v-for="item in staged"
							:key="item.id"
							:entry="item"
							@set-role="(role) => setStagedRole(item, role)"
							@update-note="(note) => setStagedNote(item, note)"
							@update-cc="(cc) => setStagedCc(item, cc)"
							@remove="unstageEntry(item)" />
					</div>

					<div class="rs-foot split">
						<button class="rs-manageb" type="button" v-test="'project-share-dialog-manage'" @click="openManage">
							<span v-html="icons.team"></span> Manage access
							<span v-if="peopleLoading" class="rs-mcount" aria-busy="true" aria-label="Loading access count">…</span>
							<span v-else class="rs-mcount">{{ peopleCount }}</span>
						</button>
						<!-- LSP-20182: no `.dis` grey and no `aria-disabled` here. Native `disabled` was never
						     set and sendStaged() already no-ops on an empty queue (repo CLAUDE.md "Form action
						     buttons — always enabled, no-op when invalid"), so presenting the control as
						     disabled contradicted what it actually does. `title` stays: a hover hint naming
						     what to add, not a disabled affordance. -->
						<button
							class="rs-shareb"
							type="button"
							v-test="'project-share-dialog-share'"
							:title="!hasStaged ? 'Add people above to share this project with them.' : undefined"
							@click="sendStaged">
							Share
						</button>
					</div>
				</div>
			</div>
		</Teleport>
		<!-- LSP-17773 (staging QA): reuse the app's existing CreateCustomerDialog rather than a
		     bespoke inline form — same flag phone input, duplicate-contact detection, validation
		     and styling as the Contacts page. openCreateContact awaits its promise and stages the
		     returned contact into the share queue. -->
		<!-- This used to also carry v-test="'project-share-dialog-create-contact'", a stray duplicate
		     of the real one on ShareInviteField.vue's "+" trigger button. Applying it here too, on a
		     component whose resolved root is a Teleport (Dialog -> VDialog -> VOverlay), produced Vue's
		     "Runtime directive used on component with non-element root node" warning — removed. -->
		<CreateCustomerDialog ref="createCustomerDialog" />
	</div>
</template>

<script>
	import { isSearchTooLong, searchFieldProps } from "@/helpers/searchLimit";
	import api from "../api";
	import { isRoleOrInRoles } from "../api/account";
	import { Role } from "@/enums/Role";
	import { hasFreeTrialEnded } from "../helpers/hasFreeTrialEnded.js";
	import { AccessType } from "@/enums/AccessType";
	import DeviceUtility from "@/helpers/device";
	import { constants } from "@/api/constants";
	import {
		calculateTimeout,
		copyToClipboard,
		countCharacters,
		debounce,
		getMessageFromError,
		isProjectAccessDeniedError
	} from "@/helpers/utilities";
	import { getApiErrorStatus } from "@/helpers/apiErrorShape";
	import { formatDelayHuman } from "@/helpers/time";
	import { createLatestRequestScope } from "@/composables/useLatestRequest";
	import { isAbortError } from "@/helpers/abort";
	import { MIN_DIRECTORY_SEARCH_LENGTH, phoneSearchPatterns, textSearchPatterns } from "@/helpers/directoryQuery";
	import EventBus from "@/helpers/eventBus";
	import { useToast } from "vue-toastification";
	import { useOrientationChange } from "@/composables/useOrientationChange";
	import { logError, logInfo, logWarn } from "@/helpers/logError";
	import { couldNot } from "@/helpers/userMessages";
	import uuid from "../helpers/uuid.js";
	import { dialCodeForCountry, toE164 } from "@/helpers/phone";
	import CreateCustomerDialog from "./CreateCustomerDialog.vue";
	import ShareInviteField from "./share/ShareInviteField.vue";
	import { emailComparisonKey, shareRowMatchesIdentity } from "@/helpers/projectAccess";
	import ShareStagedRow from "./share/ShareStagedRow.vue";
	import { shareIcons } from "./share/shareIcons";
	import { VuetifyValidation } from "@/helpers/Validation";

	const DELETED_USER = ".";
	const ALREADY_SHARED_MESSAGE = "already been shared";
	const RATE_LIMIT_FALLBACK_REASON = "You have shared this project with a lot of new people recently.";
	const RATE_LIMIT_FALLBACK_RETRY_AFTER_SECONDS = 60 * 60;
	const MAX_RATE_LIMIT_RETRY_AFTER_SECONDS = 7 * 24 * 60 * 60;
	const MAX_RATE_LIMIT_TOAST_RECIPIENTS = 5;
	// Matches ShareStagedRow's NOTE_MAX and the API's note cap — Share no-ops while a note is over.
	const STAGED_NOTE_MAX = 1000;
	// LSP-18911: after EVERY resend attempt (success or failure) the row's button stays disabled
	// for this long. Without it a failing resend could be hammered, stacking one error toast per
	// click; the cooldown is what makes "one message per failure" hold in practice.
	const RESEND_COOLDOWN_MS = 5000;
	const ERROR_MESSAGE = "Something went wrong sharing the project. Please try again.";
	// LSP-19142: rough worst-case height of the role menu (two role options + the danger "Remove
	// access" option, plus the phone-invite note some rows also render) — used only to decide
	// whether it fits below the pill on the CURRENT viewport, never rendered. Deliberately generous
	// (the menu is usually shorter) so the flip errs toward opening upward rather than clipping.
	const ROLE_MENU_ESTIMATED_HEIGHT = 220;
	const SYNTHETIC_CREATOR_ROW_ID = "synthetic-creator";
	// LSP-15692: server-side directory search tuning — replaces the pageSize-5000/50000
	// load-everything-then-filter-client-side approach from LSP-15691.
	const DIRECTORY_SEARCH_DEBOUNCE_MS = 250;
	// The row cap for BOTH halves of the directory fan-out — `api.searchCustomers` and
	// `api.getUsers` — not just the users half.
	//
	// ls-heroku-ci FULL review at 578ef71f2d (HIGH): this constant was passed only to `getUsers`
	// while `api.searchCustomers` kept its own hardcoded 10, so one fan-out over one query returned
	// two different result depths. That is user-visible, not cosmetic: the client filter is a SUBSET
	// of the server predicate, so a contact the server truncated can never be recovered
	// client-side — and because customers are ordered `createdAt DESC`, a query matching more than 10
	// contacts silently dropped the older ones while the team half still saw 20. The six rows
	// ShareInviteField renders were then drawn from two unequally-deep pools.
	//
	// Both call sites now read THIS value. Do not reintroduce a second literal: an equal-but-separate
	// number is the same defect one edit away.
	const DIRECTORY_SEARCH_PAGE_SIZE = 20;
	// AI-review finding 3 (231f07b71c): the fan-out had no watchdog. A backend that never settles
	// (dropped socket, hung dyno) meant Promise.allSettled never resolved, the finally never ran,
	// "Checking…"/"Searching…" latched forever, and onEnter's checking-variant guard made Enter a
	// permanent no-op for that query. Only a newer keystroke could break the state.
	const DIRECTORY_SEARCH_TIMEOUT_MS = 20000;
	const SUGGESTED_DIRECTORY_PAGE_SIZE = 20;
	// PR #3838 review (medium #18 / medium #7): both getUsers calls are PAGED (20 rows), so without an
	// explicit order the rows on "page 1" are whatever the planner returns — a nondeterministic
	// suggested list, and a nondeterministic membership slice for a broad query. The trailing id keeps
	// ties stable, matching the deterministic-order precedent in search/UserFilter.vue.
	const DIRECTORY_USER_ORDER = [
		["firstname", "ASC"],
		["lastname", "ASC"],
		["id", "ASC"]
	];

	export default {
		name: "ShareActionDialog",
		setup() {
			const toast = useToast();
			// LSP-16441: on a device orientation change, blur a focused search input so the
			// mobile keyboard/scroll doesn't strand the dialog. Drives the watcher below.
			const { orientationChangeCount } = useOrientationChange();
			return { toast, orientationChangeCount };
		},
		components: {
			CreateCustomerDialog,
			ShareInviteField,
			ShareStagedRow
		},
		props: {
			projectId: String,
			projectName: String,
			isDisabled: Boolean,
			access: AccessType,
			btnProps: {
				type: Object,
				default: () => ({})
			},
			// LSP-17773: a request-access deep link (?requestAccessEmail=) can open this dialog
			// with the requester's email staged into the queue.
			stagedEmail: {
				type: String,
				default: ""
			}
		},
		data() {
			return {
				dialog: false,
				// LSP-17773: prototype staged-share queue — each recipient carries its OWN role + note.
				staged: [],
				manageView: false,
				roleMenuFor: null,
				// LSP-19142: true when the role menu for `roleMenuFor` doesn't have enough room to open
				// downward (its usual `top: 100%`) without spilling past the viewport/dialog on a short
				// screen — flips it to open upward instead. Decided once, when the menu opens, from the
				// pill's own position (see shouldOpenRoleMenuUpward), not recomputed continuously.
				roleMenuOpensUpward: false,
				// True while the CreateCustomerDialog sub-dialog is open (set around its open() call).
				// onDialogKeydown reads this so Escape backing out of the sub-dialog doesn't also close
				// the share dialog and wipe the staged queue — see the comment there for the why.
				createContactSubOpen: false,
				sharedUsers: [],
				rosterCreator: null,
				// LSP-15692: the invite directory is server-searched. "initial" holds the small
				// empty-query suggested set loaded on open; "searched" holds the latest query's
				// results. The teamMembers/customers computeds pick between them.
				initialTeamMembers: [],
				initialCustomers: [],
				searchedTeamMembers: [],
				searchedCustomers: [],
				// True once a ≥2-char query's results have arrived; reverts to false when the
				// query drops under the minimum so the suggested set comes back.
				directoryQueryActive: false,
				directorySearching: false,
				// Bugbot follow-up (PR #3838): true when the CURRENT query's fan-out failed for real
				// (never set by aborted/superseded requests). While true the invite field keeps the
				// typed email in the unresolved state — a failed search leaves the slice stale, so
				// "searching === false" alone must never be read as "the slice is resolved".
				directorySearchFailed: false,
				// The latest trimmed query ("" when under the minimum). runDirectorySearch compares
				// against this at entry so a debounce timer queued before a clear/close/reopen can't
				// fire a stale search 250ms later.
				directoryQuery: "",
				// Break-it round 3: the query STRING alone is not an identity. Typing
				// alpha -> beta -> alpha makes an abandoned alpha request's string match "current"
				// again, so its stale failure toasted, latched directorySearchFailed, and stole the
				// queued retry's "checking" state. Every keystroke (and every open/close) bumps this
				// counter, so intent is identified by (query, generation), not by the string.
				directoryQueryGeneration: 0,
				// Break-it round 1 on the phone delta: a watchdog timer outlived close() and unmount for a
				// request that never settles (its own finally never runs), leaving a timer pointing at a
				// torn-down component. AI-review finding 3 (587ac43c30): a single slot was overwritten by
				// every new request, so a SUPERSEDED request's timer was orphaned and teardown could only
				// ever disarm the newest one. Every pending watchdog is tracked.
				directorySearchTimeoutHandles: [],
				directorySearchScope: createLatestRequestScope(),
				// Debounced fan-out for @query-change. Plain function so `this` binds to the
				// component when invoked as this.debouncedDirectorySearch(query). Break-it round 1:
				// recheck intent at FIRE time — the query may have been cleared/replaced, or the
				// dialog closed or re-opened, while this timer was queued.
				debouncedDirectorySearch: debounce(function (query, generation) {
					if (query !== this.directoryQuery || generation !== this.directoryQueryGeneration || !this.dialog) {
						return;
					}
					// Round-6 finding 4: this is the ONLY caller and it is fire-and-forget, so anything that
					// throws in runDirectorySearch BEFORE its try block (the trim, startRequest(), arming the
					// watchdog) would surface as an unhandled rejection — no log, no toast, and
					// directorySearching never cleared because nothing reaches the finally. Low probability
					// today, trivial for a future refactor to introduce.
					this.runDirectorySearch(query, generation).catch((error) => {
						this.directorySearching = false;
						logError("ShareActionDialog: directory search rejected before its own error handling", error, {
							projectId: this.projectId,
							query
						});
					});
				}, DIRECTORY_SEARCH_DEBOUNCE_MS),
				me: null,
				currentPage: 1,
				itemsPerPage: 5,
				shareUrl: "",
				project: null,
				createdByUser: null,
				createdByUserId: null,
				organizationAdministratorUserIds: new Set(),
				organizationAdministratorAccountIds: new Set(),
				organizationAdministratorEmails: new Set(),
				derivedAccessSummary: null,
				derivedAccessSummaryLoading: false,
				AccessType: AccessType,
				REMOVE_ACCESS: constants.REMOVE_ACCESS,
				alreadySharedUsers: [],
				sharedUsersSearchText: "",
				peopleLoading: false,
				loadSharedUsersPromise: null,
				// Monotonic counter incremented synchronously at the top of init(). Concurrent init()
				// calls overlap their await chains, so a promise-identity dedup token can be
				// overwritten by whichever call reaches its synchronous tail last. Capturing the
				// counter into a local at the top of each init() call gives every async closure a
				// stable generation to compare against — only the most recent init owns the spinner.
				loadGeneration: 0,
				resendingId: null,
				// LSP-18911: last outcome per share id ("sent" | "failed") so the button label reports
				// what actually happened instead of snapping back to "Resend invite" — the AC asks the
				// button to reflect pending / sending / failed / completed.
				resendOutcomes: {},
				// Share ids currently inside their post-attempt cooldown (see RESEND_COOLDOWN_MS).
				resendCooldowns: {},
				// Pending cooldown timers, keyed by share id, so beforeUnmount can clear them.
				resendCooldownTimers: {},
				// In-flight latch for sendStaged() — a second Share click while a batch is being
				// created is a no-op (double-submit guard, not a validity gate; see repo CLAUDE.md).
				sendingStaged: false
			};
		},
		created() {
			// Non-reactive: only the async roster refresh reads it. Ids removed during this open (LSP-22144).
			this.removedShareIds = new Set();
		},
		computed: {
			icons() {
				return shareIcons;
			},
			hasStaged() {
				return this.staged.length > 0;
			},
			// PR #3838 review H1: the invite field's empty state needs the search minimum so a
			// query too short to have been searched asks for more input instead of claiming no match.
			minDirectorySearchLength() {
				return MIN_DIRECTORY_SEARCH_LENGTH;
			},
			// LSP-15692: the directory the invite field sees — the latest search results while a
			// query is active, else the small suggested set loaded on open.
			teamMembers() {
				return this.directoryQueryActive ? this.searchedTeamMembers : this.initialTeamMembers;
			},
			customers() {
				return this.directoryQueryActive ? this.searchedCustomers : this.initialCustomers;
			},
			// LSP-21958: count the rows shown in Manage Access; the sentence still describes org-wide viewing.
			peopleCount() {
				return this.sharedUsers.length;
			},
			rosterRoleCounts() {
				const administrators = this.sharedUsers.filter((item) => this.isOrganizationAdministratorRow(item)).length;
				return { administrators, members: this.sharedUsers.length - administrators };
			},
			derivedAccessSummaryText() {
				const summary = this.derivedAccessSummary;
				if (!summary) return "";
				const organizationName = summary.organizationName || "your organization";
				const { administrators, members } = this.rosterRoleCounts;
				const counts = `(${administrators} administrators, ${members} other members)`;
				if (summary.viewers === "organization" && summary.editors === "organization-administrators") {
					return `Everyone in ${organizationName} can view this project, and organization administrators can edit it. ${counts}`;
				}
				if (summary.viewers === "organization") {
					return `Everyone in ${organizationName} can view this project. Organization administrators do not automatically receive editing access. ${counts}`;
				}
				if (summary.editors === "organization-administrators") {
					return `Organization membership in ${organizationName} does not automatically grant viewing access, but organization administrators can edit this project. ${counts}`;
				}
				return `Organization membership in ${organizationName} does not automatically grant viewing or editing access. ${counts}`;
			},
			roleMenuOptions() {
				return [
					{ value: AccessType.Viewer, label: "Viewer" },
					{ value: AccessType.Editor, label: "Editor" }
				];
			},
			// Current user's email domain — drives the invite field's "same company" detection.
			myDomain() {
				const email = this.me?.user?.email || "";
				return email.includes("@") ? email.split("@")[1] : "";
			},
			// LSP-17773 (review): "Add to team instead" routes to /users/new, which the router
			// guards behind these roles — mirror the guard here so non-admins never see a button
			// that would only bounce them to home. Same role set + case-insensitive matcher as
			// useCurrentUserRole's canManageUsers (reuse-before-reinvent; this component already
			// carries `me`, so the composable's own fetch isn't needed).
			canManageTeam() {
				const manageTeamRoles = [Role.Administrator, Role.SuperAdministrator, Role.Support];
				return (this.me?.account?.roles ?? []).some((role) => isRoleOrInRoles(role?.name, manageTeamRoles));
			},
			// Org default country (ISO) so the invite field can normalize a typed national-format
			// phone into E.164 before deduping against existing shares. Falls back to "US" (matches
			// toE164's own default) when the org has no configured country.
			orgDefaultCountry() {
				return this.me?.organization?.defaultCountryCode || "US";
			},
			// The org country's "+<dial code>" — staged rows and the create-contact modal default
			// to THIS, never a hardcoded "+1" (Bugbot): the invite field dedups a typed national
			// number against orgDefaultCountry, so staging/sending under +1 in a non-US org would
			// mint a different E.164 than the one that was deduped — and text a stranger.
			orgDefaultDialCode() {
				return dialCodeForCountry(this.orgDefaultCountry);
			},
			teamEmails() {
				return this.teamMembers.map((member) => member.email).filter((email) => typeof email === "string");
			},
			// Lower-cased identifiers already on the project — the invite field greys/blocks re-adding them.
			usedIdentifiers() {
				const out = [];
				// Normalize every existing-share phone with the ORG default country, never toE164's US
				// default — the invite field dedups a typed national number against orgDefaultCountry
				// (ShareInviteField.phoneKey), so a US-default normalization here would mint a different
				// E.164 in a non-US org and let the same number be invited twice.
				const pushPhone = (phone) => {
					const e164 = toE164(phone, this.orgDefaultCountry);
					if (e164) out.push(e164.toLowerCase());
				};
				for (const user of this.sharedUsers) {
					if (user.userIdentifier) {
						const identifier = String(user.userIdentifier);
						out.push(identifier.includes("@") ? emailComparisonKey(identifier) : identifier.toLowerCase());
					}
					const email = user.User?.email || user.EmailAddress?.emailAddress;
					if (email) {
						out.push(emailComparisonKey(String(email)));
					}
					if (user.User?.phone) {
						pushPhone(user.User.phone);
					}
					// A CUSTOMER-type share is listed by its customer's phone (getTitleList renders
					// Customer.PhoneNumbers[0].phoneNumber for the same row) — index those numbers too
					// so an already-shared contact can't be re-invited by phone.
					for (const customerPhone of user.Customer?.PhoneNumbers || []) {
						if (customerPhone?.phoneNumber) {
							pushPhone(customerPhone.phoneNumber);
						}
					}
				}
				return out;
			},
			filteredSharedUsers() {
				if (isSearchTooLong(this.sharedUsersSearchText)) return [];
				const searchText = (this.sharedUsersSearchText || "").trim().toLowerCase();
				if (!searchText) {
					return this.sharedUsers;
				}
				// Phone search must survive formatting: "+91 86504 42858" vs "+918650442828" vs a
				// national "8650442828" all name the same person, so when the query carries ≥3
				// digits compare digit-to-digit.
				//
				// NOT the same predicate as ShareInviteField.directoryHits, and the comment here used to
				// claim it was (PR #3838 review low). This filters the ALREADY-SHARED list, which is fully
				// loaded client-side; directoryHits filters SERVER results and therefore has to mirror the
				// server's ILIKE predicates, which it now does via phoneSearchCandidates — extension-aware
				// ("212-555-0100 x123" finds the base number) and with no digit floor on visibility. So an
				// extension query can match a contact in the invite search and NOT match the same person
				// in this list. The divergence is accepted rather than closed: routing this filter through
				// phoneSearchCandidates would parse every keystroke of a list filter through
				// libphonenumber for no correctness gain, and unlike directoryHits a miss here is
				// recoverable — the row is still in this.sharedUsers, one backspace away. Revisit if
				// users report that a number they can find in the invite field cannot be found in the list.
				const searchDigits = searchText.replace(/\D/g, "");
				const isPhoneShapedQuery = /^[\d\s+().-]+$/.test(searchText);
				const digitsMatch = (value) =>
					isPhoneShapedQuery &&
					searchDigits.length >= 3 &&
					typeof value === "string" &&
					value.replace(/\D/g, "").includes(searchDigits);
				// Mirror getTitle: synthetic rows always carry the Creator label; real shares match
				// the creator identity supplied by the roster or the project lookup.
				const hasCreatorIdentity = !!this.createdByUser?.Account || !!this.rosterCreator;
				const creatorAccountId = this.createdByUser?.Account?.id;
				const creatorEmail = this.createdByUser?.User?.email;
				const myAccountId = this.me?.account?.id;
				const myEmail = this.me?.user?.email;
				return this.sharedUsers.filter((item) => {
					const fields = [];
					const phoneFields = [];
					// Raw EMAIL/PHONE pending invites carry their value in ProjectShare.userIdentifier;
					// ProjectShare has no top-level email/phone/invitedEmail fields.
					fields.push(item.userIdentifier);
					if (item.userIdentifierType === "phone") {
						phoneFields.push(item.userIdentifier);
					}
					if (item.User) {
						fields.push(item.User.name, item.User.email, item.User.phone);
						phoneFields.push(item.User.phone);
					}
					if (item.Customer) {
						fields.push(item.Customer.name);
						(item.Customer.EmailAddresses || []).forEach((email) => fields.push(email?.emailAddress));
						(item.Customer.PhoneNumbers || []).forEach((phone) => {
							fields.push(phone?.phoneNumber);
							phoneFields.push(phone?.phoneNumber);
						});
					}
					if (item.EmailAddress) {
						fields.push(item.EmailAddress.emailAddress);
						const customer = item.EmailAddress.Customer;
						if (customer) {
							fields.push(customer.name);
							(customer.PhoneNumbers || []).forEach((phone) => {
								fields.push(phone?.phoneNumber);
								phoneFields.push(phone?.phoneNumber);
							});
						}
					}
					// Include the "(Creator)" / "(You)" role labels so searching those visible suffixes finds the right row.
					if (
						item.isSyntheticCreator ||
						(hasCreatorIdentity && shareRowMatchesIdentity(item.userIdentifier, [creatorAccountId, creatorEmail]))
					) {
						fields.push("Creator");
					}
					if (shareRowMatchesIdentity(item.userIdentifier, [myAccountId, myEmail])) {
						fields.push("You");
					}
					return (
						fields.some((value) => typeof value === "string" && value.toLowerCase().includes(searchText)) ||
						phoneFields.some(digitsMatch)
					);
				});
			},
			paginatedUsers() {
				const start = (this.currentPage - 1) * this.itemsPerPage;
				const end = start + this.itemsPerPage;
				return this.filteredSharedUsers.slice(start, end);
			},
			pageCount() {
				return Math.ceil(this.filteredSharedUsers.length / this.itemsPerPage);
			},
			// LSP-16739: with total-visible=0 the numbered page buttons are hidden; surface a fixed
			// "Page X of Y" counter instead, but only when there's more than one page.
			sharedUsersPageLabel() {
				return this.pageCount > 1 ? `Page ${this.currentPage} of ${this.pageCount}` : "";
			},
			// Directory the invite field searches. Existing shares remain visible so a search can explain
			// that the person already has access instead of incorrectly reporting no match.
			memberItems() {
				let team = [];
				const teamMembers = this.teamMembers;
				team.push(
					...teamMembers.map((user) => {
						const name = `${user.firstname} ${user.lastname}`.trim();
						const email = user.email;
						// A team member's phone/email are SCALAR columns on Users, and
						// buildUserSearchWhere matches exactly those two columns — so unlike a customer
						// there is no second value the server could have matched, and the one-element
						// arrays below are the complete match set rather than a truncation.
						return {
							id: user.Account.id,
							name: name.length > 0 ? name : email,
							email,
							phone: user.phone,
							phones: user.phone ? [user.phone] : [],
							emails: email ? [email] : []
						};
					})
				);
				this.customers.forEach((customer) => {
					// Bugbot MEDIUM (thread 3861233836): `phones`/`emails` carry EVERY value the server
					// could have matched on, because `api.searchCustomers` filters on the ASSOCIATION
					// columns `$PhoneNumbers.phonenumberraw$` and `$EmailAddresses.emailAddress$` — a
					// Sequelize join, so a customer is returned when ANY of their numbers or addresses
					// matches. Passing only `PhoneNumbers[0]` / `EmailAddresses[0]` meant the client filter
					// re-tested a single value and HID a row the server had correctly returned, so a
					// contact searched by their SECOND number fell through to a raw "Invite <number>" row
					// with no customerId — the share was never linked to the existing customer record.
					// That is exactly the client/server agreement ShareInviteField's filter comment claims
					// to hold, so the arrays are what makes the claim true rather than aspirational.
					//
					// The SCALARS stay the primary identity and keep their exact former values: they are
					// what gets STAGED (the number actually texted, the address actually emailed), what the
					// already-shared dedup compares, and what the subtitle renders. Only MATCHING widens.
					const phones = (customer.PhoneNumbers || []).map((entry) => entry?.phoneNumber).filter(Boolean);
					const emails = (customer.EmailAddresses || []).map((entry) => entry?.emailAddress).filter(Boolean);
					if (!customer.EmailAddresses || customer.EmailAddresses.length == 0) {
						if (customer.PhoneNumbers && customer.PhoneNumbers.length > 0) {
							team.push({
								id: customer.id,
								name: customer.name.length > 0 ? customer.name : customer.PhoneNumbers[0]?.phoneNumber,
								phone: customer.PhoneNumbers[0]?.phoneNumber,
								phones
							});
						}
						return;
					}
					team.push({
						id: customer.id,
						name: customer.name.length > 0 ? customer.name : customer.EmailAddresses[0]?.emailAddress,
						email: customer.EmailAddresses[0]?.emailAddress,
						phone: customer.PhoneNumbers[0]?.phoneNumber,
						phones,
						emails
					});
				});
				return team;
			}
		},
		watch: {
			dialog(isOpen) {
				if (isOpen) {
					this.scheduleDialogFocus();
				}
			},
			manageView() {
				if (this.dialog) {
					this.scheduleDialogFocus();
				}
			},
			// LSP-16441: re-blur a focused search input each time the device orientation changes.
			orientationChangeCount() {
				this.handleOrientationChange();
			},
			pageCount: {
				handler(newVal) {
					if (newVal < 1) {
						this.currentPage = 1;
					} else if (newVal < this.currentPage) {
						this.currentPage = newVal;
					}
				}
			},
			sharedUsersSearchText() {
				this.currentPage = 1;
			}
		},
		// This is a hand-rolled overlay (not the shared Dialog.vue), so Escape and focus containment
		// are owned here (role/aria-modal live on the .rs-dlg template).
		mounted() {
			// CAPTURE phase (the `true`) so this runs window→target, BEFORE the CreateCustomerDialog's
			// own Escape handler — see onDialogKeydown for why that ordering is load-bearing.
			window.addEventListener("keydown", this.onDialogKeydown, true);
		},
		beforeUnmount() {
			// A keyed project replacement retires this dialog's pending loads.
			this.loadGeneration += 1;
			// Must pass the same capture flag or the listener won't be removed.
			window.removeEventListener("keydown", this.onDialogKeydown, true);
			// LSP-18911: a cooldown timer outliving the component would fire against a torn-down
			// instance, so drop every pending one here.
			for (const timer of Object.values(this.resendCooldownTimers)) {
				clearTimeout(timer);
			}
			this.resendCooldownTimers = {};
			// PR #3838 review (medium #3): drop the queued debounce AND the in-flight request pair.
			// Without this a search could settle after teardown and toast against a dead component.
			// Clearing directoryQuery also neuters any timer that somehow still fires (the debounced
			// wrapper rechecks intent at fire time).
			this.directoryQuery = "";
			this.directoryQueryGeneration += 1;
			this.clearDirectorySearchTimeout();
			this.debouncedDirectorySearch.cancel();
			this.directorySearchScope.abortCurrent();
		},
		methods: {
			searchFieldProps,
			scheduleDialogFocus() {
				this.$nextTick(() => {
					// The surrounding Vuetify tooltip closes in the same render cycle and can blur its
					// activator after this dialog mounts. Run after that cleanup so focus stays modal.
					window.setTimeout(() => this.focusActiveDialog(), 0);
				});
			},
			focusableDialogElements() {
				const dialogPanel = this.$refs.dialogPanel;
				if (!dialogPanel) return [];
				return Array.from(
					dialogPanel.querySelectorAll(
						'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
					)
				).filter((element) => {
					if (element.closest('[aria-hidden="true"]')) return false;
					for (let current = element; current && current !== dialogPanel.parentElement; current = current.parentElement) {
						const style = window.getComputedStyle(current);
						if (style.display === "none" || style.visibility === "hidden") return false;
					}
					return true;
				});
			},
			focusActiveDialog() {
				if (!this.dialog || this.createContactSubOpen) return;
				const dialogPanel = this.$refs.dialogPanel;
				if (!dialogPanel) return;
				if (dialogPanel.contains(document.activeElement)) return;
				(this.focusableDialogElements()[0] || dialogPanel).focus();
			},
			trapDialogFocus(event) {
				const dialogPanel = this.$refs.dialogPanel;
				if (!dialogPanel) return;
				const focusableElements = this.focusableDialogElements();
				if (focusableElements.length === 0) {
					event.preventDefault();
					dialogPanel.focus();
					return;
				}
				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];
				const focusIsInside = dialogPanel.contains(document.activeElement);
				const dialogPanelHasFocus = document.activeElement === dialogPanel;
				if (event.shiftKey && (!focusIsInside || dialogPanelHasFocus || document.activeElement === firstElement)) {
					event.preventDefault();
					lastElement.focus();
				} else if (!event.shiftKey && (!focusIsInside || dialogPanelHasFocus || document.activeElement === lastElement)) {
					event.preventDefault();
					firstElement.focus();
				}
			},
			onDialogKeydown(event) {
				if (!this.dialog) return;
				if (event.key === "Tab" && !this.createContactSubOpen) {
					this.trapDialogFocus(event);
					return;
				}
				if (event.key !== "Escape") return;
				// Don't tear down the share (and lose the staged queue) when the user presses Escape to
				// dismiss the CreateCustomerDialog sub-dialog. The latch is set around open() (below).
				// For a REAL (trusted) keypress the JS stack empties between listeners, so the microtask
				// that clears the latch runs BETWEEN the child's handler and a bubble-phase window
				// listener — which is why an earlier bubble-phase guard read false and failed (Jason's
				// engine trace). This listener is registered in CAPTURE phase (mounted), so it runs
				// FIRST, before anything can clear the latch — deterministic, no microtask dependence.
				if (this.createContactSubOpen) return;
				this.close();
			},
			toTitleCase(value) {
				return (value || "").replace(/[._-]/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
			},
			// ---------- staged share queue ----------
			stageEntry(entry) {
				const email = entry.email ? emailComparisonKey(entry.email) : "";
				const phone = (entry.phone || "").toLowerCase();
				if (email && this.sharedUsers.some((user) => shareRowMatchesIdentity(user.userIdentifier, [entry.email]))) {
					const message = `${entry.name} already has access to this project.`;
					this.toast.info(message, { timeout: calculateTimeout(message) });
					return;
				}
				if (email && this.organizationAdministratorEmails.has(email)) {
					const message = `${entry.name} is an organization administrator and already has full access to this project.`;
					this.toast.info(message, { timeout: calculateTimeout(message) });
					return;
				}
				if (email && this.staged.some((item) => item.email && emailComparisonKey(item.email) === email)) {
					const message = `${entry.name} is already in your list.`;
					this.toast.info(message, { timeout: calculateTimeout(message) });
					return;
				}
				if (phone) {
					// F12: compare phones in E.164 (the same normalization sendStaged uses) so one number
					// typed in two formats (e.g. "212 555 0100" and "+12125550100") can't both stage.
					// Fall back to the raw string only when a side doesn't normalize.
					const entryPhoneE164 = this.stagedPhoneE164(entry);
					const isAlreadyStaged = this.staged.some((item) => {
						if (!item.phone) {
							return false;
						}
						const itemPhoneE164 = this.stagedPhoneE164(item);
						if (entryPhoneE164 && itemPhoneE164) {
							return itemPhoneE164 === entryPhoneE164;
						}
						return (item.phone || "").toLowerCase() === phone;
					});
					if (isAlreadyStaged) {
						const message = `${entry.name} is already in your list.`;
						this.toast.info(message, { timeout: calculateTimeout(message) });
						return;
					}
				}
				this.staged.push({
					id: `s${Date.now()}${Math.floor(Math.random() * 999)}`,
					role: null,
					cc: this.orgDefaultDialCode,
					note: "",
					...entry
				});
			},
			unstageEntry(item) {
				this.staged = this.staged.filter((staged) => staged !== item);
			},
			setStagedRole(item, role) {
				item.role = role;
			},
			setStagedNote(item, note) {
				item.note = note;
			},
			setStagedCc(item, cc) {
				item.cc = cc;
			},
			// Assemble a staged phone recipient into E.164, honouring the row's country-code selection.
			stagedPhoneE164(item) {
				const raw = (item.phone || "").trim();
				if (!raw) {
					return null;
				}
				const withCountry = raw[0] === "+" ? raw : `${item.cc || this.orgDefaultDialCode}${raw.replace(/[^0-9]/g, "")}`;
				return toE164(withCountry);
			},
			// ---------- create contact (reuses the shared CreateCustomerDialog) ----------
			async openCreateContact() {
				const dialog = this.$refs.createCustomerDialog;
				if (!dialog?.open) {
					// Diagnostic-logging rule (Jason): a missing ref/method would otherwise make the
					// button do nothing silently — log so a future incident is triage-able.
					logError("ShareActionDialog: CreateCustomerDialog ref/open() unavailable", new Error("createCustomerDialog missing"), {
						projectId: this.projectId
					});
					return;
				}
				// The existing dialog owns the form, flag phone input, validation and duplicate
				// detection; it resolves with the created (or duplicate-matched) customer, or false on
				// cancel. Seed the name only from a people-search term that isn't an email (Jason:
				// resetForm treats any non-numeric seed as a name, so an email would become firstname).
				const seed = (this.sharedUsersSearchText || "").includes("@") ? "" : this.sharedUsersSearchText || "";
				// Latch a local flag so an Escape that dismisses the sub-dialog doesn't also close the
				// share dialog (see onDialogKeydown). Cleared in finally after open() resolves.
				this.createContactSubOpen = true;
				let record;
				try {
					record = await dialog.open(seed);
				} finally {
					this.createContactSubOpen = false;
				}
				if (!record) {
					return;
				}
				this.stageCreatedContact(record);
			},
			// Map a customer record from CreateCustomerDialog into the share queue. The dialog requires
			// a phone, so PhoneNumbers[0] is present. The stored value is formatInternational() — a
			// leading "+" with spaces, not bare E.164 — which stagedPhoneE164 handles via its
			// leading-"+" branch (Jason), so no cc is needed.
			stageCreatedContact(record) {
				const email = (record?.EmailAddresses?.[0]?.emailAddress || "").trim();
				const phone = (record?.PhoneNumbers?.[0]?.phoneNumber || "").trim();
				const company = (record?.companyName || "").trim();
				const name = [record?.firstname, record?.lastname].filter(Boolean).join(" ").trim() || company || email || phone;
				this.stageEntry(
					email
						? { name, email, customerId: record?.id, contact: true, company }
						: { name, phone, phoneOnly: true, customerId: record?.id, contact: true, company }
				);
			},
			// LSP-16441: on orientation change, blur whichever of the dialog's text inputs currently
			// holds focus — the people-search AND the invite field — so a rotate doesn't strand the
			// mobile keyboard/scroll. Handles both a component ref (.$el wrapper) and a plain <input> ref.
			handleOrientationChange() {
				const blurIfActive = (target) => {
					const root = target?.$el ?? target;
					const input = root?.tagName === "INPUT" ? root : root?.querySelector?.("input");
					if (input && document.activeElement === input) {
						input.blur();
					}
				};
				blurIfActive(this.$refs.customerSearch);
				blurIfActive(this.$refs.inviteField);
			},
			// ---------- manage-access view ----------
			openManage() {
				this.closeRoleMenu();
				// Yasir #9: clear any residual filter (e.g. one set by an access-request deep link) so a
				// manual open of Manage Access shows everyone, not a single filtered row.
				this.sharedUsersSearchText = "";
				this.manageView = true;
			},
			// LSP-17773 (QA): consume a request-access deep link (?requestAccessEmail=). The requester
			// MAY already hold a share (from a prior grant the owner or an admin made) — the endpoint
			// itself does NOT create one (creating a grant from an unauthenticated request was a security
			// hole; see projectShare.requestProjectAccess). So: if a matching row exists, open Manage
			// Access filtered to it for a one-tap Viewer→Editor flip; OTHERWISE (the common first-time
			// case) fall back to staging the email into the add-new queue for the OWNER to create the
			// share. Runs AFTER sharedUsers loads so the match can see existing rows.
			focusOrStageRequestedEmail() {
				const email = (this.stagedEmail || "").trim();
				// No deep link on this open (the common case — every ordinary Share open runs this) →
				// return silently. Only a PRESENT-but-invalid stagedEmail is a dropped request; logging
				// the absent case would fire on every share open and drown the real signal (Cursor).
				if (!email) {
					return;
				}
				if (VuetifyValidation.EmailValidation("Invalid email.").isValid(email) !== true) {
					logInfo("[ShareDialog] access-request deep link ignored: invalid stagedEmail", {
						projectId: this.projectId
					});
					return;
				}
				const target = email.toLowerCase();
				// Cursor: a requester who already CLAIMED their invite has an ACCOUNT row
				// (userIdentifier = accountId), so the email lives in the resolved relations, not
				// userIdentifier. Match every place the row can carry an email — mirroring the
				// filteredSharedUsers haystack exactly (incl. Customer.EmailAddresses, which is dead
				// against today's getProjectShares includes but kept aligned with the sibling filter so
				// the two can't drift if the include is widened) — so a claimed requester lands on the row.
				const rowEmails = (user) => {
					const emails = [];
					if (user.userIdentifierType === "email" && user.userIdentifier) emails.push(user.userIdentifier);
					if (user.User?.email) emails.push(user.User.email);
					if (user.EmailAddress?.emailAddress) emails.push(user.EmailAddress.emailAddress);
					(user.Customer?.EmailAddresses || []).forEach((entry) => entry?.emailAddress && emails.push(entry.emailAddress));
					return emails;
				};
				const match = this.sharedUsers.find((user) => rowEmails(user).some((value) => String(value).toLowerCase() === target));
				if (match) {
					this.closeRoleMenu();
					this.manageView = true;
					// Filter the manage list to the requester's row so it surfaces regardless of pagination.
					// (No scrollIntoView: the rows live under v-if="!peopleLoading", which is still true on
					// this microtask, so a querySelector would no-op anyway — Yasir #6. The search filter
					// alone reliably surfaces the row; openManage() clears it on the owner's next visit.)
					this.sharedUsersSearchText = email;
				} else {
					this.stageEntry({ name: this.toTitleCase(email.split("@")[0]), email });
				}
			},
			toggleRoleMenu(item, event) {
				if (this.isAccessTypeDisabled(item)) {
					return;
				}
				if (this.roleMenuFor === item.id) {
					this.closeRoleMenu();
					return;
				}
				this.roleMenuFor = item.id;
				// LSP-19142: decide the open direction from the PILL that was just clicked, not the menu
				// itself — the menu doesn't exist in the DOM until `roleMenuFor` renders it.
				this.roleMenuOpensUpward = this.shouldOpenRoleMenuUpward(event?.currentTarget);
			},
			// LSP-19142: the role menu opens `top: 100%` (below the pill) by default. For a row near the
			// BOTTOM of the paginated Manage Access list — especially on a short/mobile viewport where
			// the dialog's own `max-height` leaves little room — that pushed the menu (role options +
			// the phone-invite note + "Remove access") past the dialog's visible bounds, clipping or
			// overlapping whatever the scrim reveals below it. Flip it to open upward when the pill
			// doesn't have ROLE_MENU_ESTIMATED_HEIGHT of room beneath it — but only when flipping
			// actually helps: on a short/landscape viewport there may be even less room above than
			// below, in which case flipping up would clip the menu off the TOP of the screen instead
			// (Cursor Bugbot finding on PR #4538). Only flip when there's strictly more room above.
			shouldOpenRoleMenuUpward(pillElement) {
				if (!pillElement || typeof pillElement.getBoundingClientRect !== "function") {
					return false;
				}
				const pillRect = pillElement.getBoundingClientRect();
				const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
				const spaceBelow = viewportHeight - pillRect.bottom;
				const spaceAbove = pillRect.top;
				return spaceBelow < ROLE_MENU_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;
			},
			closeRoleMenu() {
				this.roleMenuFor = null;
				this.roleMenuOpensUpward = false;
			},
			chooseRole(item, value) {
				this.closeRoleMenu();
				this.handleOptionChange(item, value);
			},
			personRoleLabel(item) {
				return item.accessType === AccessType.Editor ? "Editor" : "Viewer";
			},
			hasDerivedAdministratorAccess(item) {
				const callerOrganizationId = this.me?.organization?.id;
				const projectOrganizationId = this.project?.organizationId;
				const belongsToCallerOrganization =
					!callerOrganizationId || !projectOrganizationId || projectOrganizationId === callerOrganizationId;
				return (
					belongsToCallerOrganization &&
					((!!item?.User?.id && this.organizationAdministratorUserIds.has(item.User.id)) ||
						[item?.accountId, item?.claimedBy, item?.userIdentifierType === "account" ? item.userIdentifier : undefined].some(
							(accountId) => !!accountId && this.organizationAdministratorAccountIds.has(accountId)
						))
				);
			},
			isOrganizationAdministratorRow(item) {
				if (this.hasDerivedAdministratorAccess(item)) return true;
				const email = item?.userIdentifierType === "email" ? emailComparisonKey(item.userIdentifier ?? "") : "";
				return !!email && this.organizationAdministratorEmails.has(email);
			},
			isAccessTypeDisabled(item) {
				if (item?.isSyntheticCreator) return true;
				return (
					this.access !== AccessType.Editor ||
					(!!this.createdByUserId && item?.User?.id === this.createdByUserId) ||
					shareRowMatchesIdentity(item?.userIdentifier, [this.createdByUser?.Account?.id, this.createdByUser?.User?.email]) ||
					// Organization administrators always retain their derived access, including when the creator manages sharing.
					this.hasDerivedAdministratorAccess(item)
				);
			},
			userHasEmail(user) {
				return (
					user.email || user.User?.email || user.Customer?.EmailAddresses?.[0]?.emailAddress || user.EmailAddress?.emailAddress
				);
			},
			async getShareUrlOfProject() {
				if (!this.project.shareId) {
					const shareId = uuid.uuidv4();
					this.project = await api.updateProject(this.projectId, {
						shareId,
						genericAccessType: AccessType.Viewer
					});
				}
				return this.project.shareUrl;
			},
			async init(evt) {
				// Bump the dedup counter BEFORE any await so concurrent init() calls can't overwrite each other's slot.
				const myGeneration = ++this.loadGeneration;
				this.removedShareIds.clear();
				const projectId = this.projectId;
				evt.currentTarget.blur();
				// LSP-17773: reset prototype dialog state synchronously so a re-open starts clean.
				this.staged = [];
				this.manageView = false;
				this.roleMenuFor = null;
				this.roleMenuOpensUpward = false;
				// Reset shared-users state synchronously so a re-open for a different project doesn't briefly render the previous list while the new data loads.
				this.sharedUsers = [];
				this.rosterCreator = null;
				this.organizationAdministratorUserIds = new Set();
				this.organizationAdministratorAccountIds = new Set();
				this.organizationAdministratorEmails = new Set();
				this.derivedAccessSummary = null;
				this.derivedAccessSummaryLoading = false;
				this.sharedUsersSearchText = "";
				// LSP-15692: drop any previous open's directory search state, queued debounce
				// intent, and in-flight requests.
				this.directoryQuery = "";
				this.directoryQueryGeneration += 1;
				this.clearDirectorySearchTimeout();
				this.debouncedDirectorySearch.cancel();
				this.directorySearchScope.abortCurrent();
				this.directoryQueryActive = false;
				this.directorySearching = false;
				this.directorySearchFailed = false;
				this.searchedTeamMembers = [];
				this.searchedCustomers = [];
				// AI-review finding 4 (d2c63ff3fe): the "clear before awaiting" inside loadTeamMembers /
				// loadCustomers closes the window during THEIR fetches, but dialog is set true and
				// getProject awaited before either runs — so for that whole round-trip the computeds still
				// returned the PREVIOUS open's suggested lists, and a fast typist could dedup against the
				// wrong project's contacts. Clear them here, synchronously with the open.
				this.initialTeamMembers = [];
				this.initialCustomers = [];
				// The dialog becomes visible before getProject resolves. Clear the previous
				// project synchronously so Copy link remains disabled until this open's URL
				// source is available (LSP-20861).
				this.project = null;
				// Activate the skeleton synchronously — the always-expanded People section would otherwise render
				// `sharedUsers=[]` with no loading indicator during the getMe/getProject/loadTeamMembers/loadCustomers await chain.
				this.peopleLoading = true;
				let loadStarted = false;
				try {
					this.me = await api.getMe();
					this.currentPage = 1;
					if (await hasFreeTrialEnded(this.me.organization, this.me.organizationSubscription)) {
						this.dialog = false;
						this.$root.$freeTrialEndedDialog.open(this.me.organization);
						// heroku #19: the share dialog never renders on this path — don't fire
						// loadSharedUsers() below with stale createdByUserId/project from a previous
						// open. The finally clears peopleLoading (loadStarted stays false).
						return false;
					} else {
						this.dialog = true;
						this.$emit("dialog-opened");
						// LSP-17773 (QA): a request-access deep link (?requestAccessEmail=) is consumed
						// AFTER the shared-users list loads, in focusOrStageRequestedEmail() — if the
						// requester already has a row (from a prior grant) we open Manage Access on it to
						// flip Viewer→Editor, otherwise we stage the email for the owner to create the share.
						// Consuming it BEFORE the list loaded was the bug: the dedup check saw an empty
						// sharedUsers and staged a duplicate for someone who already had access.
						// LSP-21223: the suggested team members and contacts don't depend on the project, so
						// fetch them while getProject is in flight. The no-op catch covers the getProject
						// failure path, which returns before anything awaits them; the await below still
						// sees the rejection.
						const directoryLoad = Promise.all([this.loadTeamMembers(), this.loadCustomers()]);
						directoryLoad.catch(() => {});
						try {
							const project = await api.getProject(projectId);
							if (myGeneration !== this.loadGeneration || projectId !== this.projectId || !this.dialog) {
								logInfo("ShareActionDialog: discarding obsolete project lookup", { projectId });
								return false;
							}
							this.project = project;
						} catch (err) {
							if (myGeneration !== this.loadGeneration || projectId !== this.projectId || !this.dialog) {
								logInfo("ShareActionDialog: discarding obsolete project failure", { projectId });
								return false;
							}
							logError("ShareActionDialog: requested project could not be opened", err, { projectId });
							this.close(true);
							const message = couldNot("open this sharing request");
							this.toast.error(message, { timeout: calculateTimeout(message) });
							return false;
						}
						this.createdByUserId = this.project.createdBy ?? this.project.User?.id;
						this.createdByUser = this.project.User ? { User: this.project.User, Account: this.project.User.Account } : null;
						// LSP-21223: the people list and the deep-link staging read the organization-administrator
						// sets (an admin's row is not editable and an admin's request is not staged), so load
						// those first. Then start the people list without waiting for the team/contact lists or
						// the share URL.
						try {
							await this.loadOrganizationAdministrators();
						} catch (err) {
							logError("ShareActionDialog: loadOrganizationAdministrators failed", err, { projectId: this.projectId });
							const message = "We couldn't load the team and contact list. Please try again.";
							this.toast.error(message, { timeout: calculateTimeout(message) });
							return false;
						}
						this.startSharedUsersLoad(myGeneration);
						loadStarted = true;
						try {
							await directoryLoad;
						} catch (err) {
							logError("ShareActionDialog: loadCustomers/loadTeamMembers failed", err, { projectId: this.projectId });
							const message = "We couldn't load the team and contact list. Please try again.";
							this.toast.error(message, { timeout: calculateTimeout(message) });
							return myGeneration === this.loadGeneration && this.dialog;
						}
						const promiseResponses = await Promise.all([this.getShareUrlOfProject()]);
						this.shareUrl = promiseResponses[0];
					}
				} finally {
					if (!loadStarted && myGeneration === this.loadGeneration) {
						this.peopleLoading = false;
					}
					// Opening the parent Vuetify tooltip and this teleported dialog are one click
					// flow. Reassert containment after that async open finishes, but never move a
					// user who has already placed focus inside the active dialog.
					if (myGeneration === this.loadGeneration && this.dialog) {
						this.scheduleDialogFocus();
					}
				}
				return myGeneration === this.loadGeneration && this.dialog;
			},
			// Load the people list for this open and, once it lands, consume an access-request deep link.
			startSharedUsersLoad(myGeneration) {
				const promise = this.loadSharedUsers();
				this.loadSharedUsersPromise = promise;
				// LSP-17773 (QA): once sharedUsers has loaded, land an access-request deep link on the
				// requester's existing Manage Access row (see focusOrStageRequestedEmail). Kept on its
				// OWN chain so it doesn't add a microtask hop to the peopleLoading finally below.
				const consumeDeepLink = (afterLoadFailure) => {
					// Skip a stale/closed generation, but LOG it (heroku #4): otherwise a deep link is
					// silently dropped when the dialog was re-opened or closed before the list loaded.
					if (myGeneration !== this.loadGeneration || !this.dialog) {
						if (this.stagedEmail) {
							logInfo("[ShareDialog] access-request deep link dropped: dialog stale/closed before list loaded", {
								projectId: this.projectId,
								afterLoadFailure
							});
						}
						return;
					}
					// Wrap so a throw here can't escape as an unhandled rejection (heroku #2): with the
					// two-arg then() below the failure handler no longer catches success-path throws.
					try {
						this.focusOrStageRequestedEmail();
					} catch (err) {
						logError("ShareActionDialog: consuming access-request deep link threw", err, { projectId: this.projectId });
					}
				};
				// heroku #2: two-arg then() so the failure handler runs ONLY on a genuine
				// loadSharedUsers rejection, never on a throw inside the success path.
				promise.then(
					() => consumeDeepLink(false),
					(err) => {
						// Yasir #7: even if the list fails to load, don't silently drop the deep-link email —
						// consumeDeepLink stages it (no rows to match) so the owner isn't forced to retype it.
						// Only log the fallback when there IS a deep link — the load failure itself is
						// already surfaced by the primary chain's toast/logError (Cursor: don't log on
						// every people-list failure).
						if (this.stagedEmail) {
							logInfo("[ShareDialog] loadSharedUsers failed; staging access-request deep-link email as fallback", {
								projectId: this.projectId,
								error: err?.message
							});
						}
						consumeDeepLink(true);
					}
				);
				promise
					.catch((err) => {
						logError("ShareActionDialog: loadSharedUsers failed", err, { projectId: this.projectId });
						if (myGeneration === this.loadGeneration && this.dialog) {
							const message = "We couldn't load the people with access to this project. Please try again.";
							this.toast.error(message, { timeout: calculateTimeout(message) });
						}
					})
					.finally(() => {
						if (myGeneration === this.loadGeneration) {
							this.peopleLoading = false;
						}
					});
			},
			close(requestUnavailable = false) {
				this.dialog = false;
				this.$emit("dialog-closed", { requestUnavailable: requestUnavailable === true, loadGeneration: this.loadGeneration });
				this.manageView = false;
				this.roleMenuFor = null;
				this.roleMenuOpensUpward = false;
				// LSP-15692: cancel any in-flight directory search and queued debounce intent when
				// the dialog closes.
				// PR #3838 review (medium #8): reset the RESULT state too, mirroring init(). Leaving
				// directoryQueryActive/searchedTeamMembers/searchedCustomers behind meant a re-open
				// could apply the previous open's slice to a fresh query.
				this.directoryQuery = "";
				this.directoryQueryGeneration += 1;
				this.clearDirectorySearchTimeout();
				this.debouncedDirectorySearch.cancel();
				this.directorySearchScope.abortCurrent();
				this.directoryQueryActive = false;
				this.directorySearching = false;
				this.directorySearchFailed = false;
				this.searchedTeamMembers = [];
				this.searchedCustomers = [];
			},
			// LSP-17773 (review): "Add to team instead" previously just closed the suggestion
			// panel — a dead end. Hand off to the Users page's OWN add flow via ?addUser=
			// (Bugbot: the seat/upgrade-consent checks live in UsersPage.add(), NOT on the Add
			// User page, so deep-linking /users/new would let an at-capacity org mint a billable
			// seat without the upgrade dialog). UsersPage runs add() on arrival and prefills the
			// email once the checks pass.
			onAddToTeam(email) {
				// Review high #2: navigating away silently DISCARDS the staged share queue (staged is
				// only cleared by the next open's init(), so the loss is invisible until then, and
				// each entry carries its own role/note). Fail safe: keep the dialog open and tell the
				// user — share or remove the staged people first, then come back to this button.
				if (this.hasStaged) {
					const stagedWarning =
						'Share or remove the people under "Ready to share" first — adding to your team now would discard them.';
					this.toast.warning(stagedWarning, { timeout: calculateTimeout(stagedWarning) });
					return;
				}
				this.close();
				this.$router.push({ path: "/users", query: { addUser: email } }).catch((error) => {
					// Same guarded-push convention as this file's other navigations: a rejected
					// push (e.g. a navigation guard) must not surface as an unhandled rejection — and
					// the dialog is already closed, so the user gets a toast, not just a log
					// (review medium #7).
					logError("ShareActionDialog: add-to-team navigation failed", error);
					const navigationFailedMessage = "We couldn't open the team page. Please go to Settings → Users to add them.";
					this.toast.error(navigationFailedMessage, { timeout: calculateTimeout(navigationFailedMessage) });
				});
			},
			getTitle(item) {
				let appendText = "";
				// shareRowMatchesIdentity requires the row's userIdentifier to be PRESENT: a loose ==
				// against an undefined me/creator field mislabeled identifier-less rows as
				// "(You)"/"(Creator)" (same undefined-equality family as the self-removal bug).
				if (
					item.isSyntheticCreator ||
					((this.createdByUser?.Account || this.rosterCreator) &&
						shareRowMatchesIdentity(item.userIdentifier, [this.createdByUser?.Account?.id, this.createdByUser?.User?.email]))
				) {
					appendText = " (Creator)";
				} else if (shareRowMatchesIdentity(item.userIdentifier, [this.me?.account?.id, this.me?.user?.email])) {
					appendText = " (You)";
				}
				const titleList = this.getTitleList(item);
				return titleList[0] + appendText;
			},
			getTitleList(item) {
				const finalTitles = new Set();
				if (item.User) {
					if (item.User.name) finalTitles.add(item.User.name);
					if (item.User.email) finalTitles.add(item.User.email);
					if (item.User.phone) finalTitles.add(item.User.phone);
				}
				if (item.Customer) {
					if (item.Customer.name) finalTitles.add(item.Customer.name);
					if (item.Customer.EmailAddresses?.[0]?.emailAddress) {
						finalTitles.add(item.Customer.EmailAddresses[0].emailAddress);
					}
					if (item.Customer.PhoneNumbers?.[0]?.phoneNumber) {
						finalTitles.add(item.Customer.PhoneNumbers[0].phoneNumber);
					}
				}
				if (item.EmailAddress?.emailAddress) {
					const customer = item.EmailAddress.Customer;
					if (customer) {
						if (customer.name) finalTitles.add(customer.name);
						finalTitles.add(item.EmailAddress.emailAddress);
						if (customer.PhoneNumbers && customer.PhoneNumbers.length > 0 && customer.PhoneNumbers[0]?.phoneNumber) {
							finalTitles.add(customer.PhoneNumbers[0]?.phoneNumber);
						}
					} else {
						finalTitles.add(item.EmailAddress.emailAddress);
					}
				}
				// LSP-17773: a raw EMAIL/PHONE pending invite has no User/Customer/EmailAddress —
				// fall back to the invited identifier so it renders (e.g. "uditwork28@gmail.com" /
				// "+91…") instead of collapsing to DELETED_USER and being filtered out of the list.
				if (
					finalTitles.size === 0 &&
					item.userIdentifier &&
					(item.userIdentifierType === "email" || item.userIdentifierType === "phone")
				) {
					finalTitles.add(item.userIdentifier);
				}
				return finalTitles.size > 0 ? Array.from(finalTitles) : [DELETED_USER];
			},
			getSubtitle(item) {
				const subtitleList = this.getTitleList(item);
				const subtitleListSet = new Set(subtitleList.map((title) => title.trim()));
				return Array.from(subtitleListSet).slice(1).join(" | ");
			},
			async handleOptionChange(item, value) {
				if (item?.isSyntheticCreator) {
					logWarn("ShareActionDialog: ignoring share mutation on synthetic creator row", {
						projectId: this.projectId,
						action: "handleOptionChange"
					});
					return;
				}
				if (value === constants.REMOVE_ACCESS.value) {
					const originalAccessType = item.accessType;
					let removalMessage = "This will remove access for " + this.getTitle(item) + ".";
					// shareRowMatchesIdentity (helpers/projectAccess): a loose == with an undefined
					// userIdentifier matched an undefined me field and showed "remove YOUR access"
					// for someone else's row.
					if (shareRowMatchesIdentity(item.userIdentifier, [this.me?.user?.email, this.me?.account?.id])) {
						removalMessage = "This will remove your access to the project.";
					}
					const result = await this.$root.$confirm.open("Remove Access", removalMessage, false, "Remove", "Cancel", true);
					if (result) {
						const previousSharedUsers = [...this.sharedUsers];
						// Remembered for this open: a roster refresh that started before the DELETE can still resolve after
						// this optimistic removal and carry the old list, so any refresh that returns this id has it dropped
						// (LSP-22144) instead of the removed person reappearing in Manage Access and the share search.
						this.removedShareIds.add(item.id);
						this.sharedUsers = this.sharedUsers.filter((u) => u.id !== item.id);
						try {
							await api.deleteProjectShare(item.id);
							EventBus.$emit("UserRemovedFromProject", item, this.projectId);
						} catch (error) {
							item.accessType = originalAccessType;
							// A failed delete must let the restored share through the following refresh.
							this.removedShareIds.delete(item.id);
							this.sharedUsers = previousSharedUsers;
							const message = error?.message || "Something went wrong trying to remove access. Please refresh and try again.";
							if (isProjectAccessDeniedError(error)) {
								this.toast.info("Your access changed. Refresh the project and try again.");
							} else {
								this.toast.error(message, { timeout: calculateTimeout(message) });
							}
							// heroku #21: this refresh is fire-and-forget — catch its failure like the
							// file's other floating loadSharedUsers() calls (sendStaged's tail) so a
							// second failure can't surface as an unhandled rejection.
							this.loadSharedUsers().catch((err) =>
								logError("ShareActionDialog: post-remove loadSharedUsers refresh failed", err, {
									projectId: this.projectId
								})
							);
							return;
						}
					} else {
						item.accessType = originalAccessType;
						return;
					}
				} else {
					const previousAccessType = item.accessType;
					try {
						await api.updateProjectShare(item.id, { accessType: value });
						item.accessType = value;
						EventBus.$emit("ProjectShareAccessChanged", item, this.projectId, value);
					} catch (error) {
						item.accessType = previousAccessType;
						const message = error?.message || "Something went wrong updating access. Please refresh and try again.";
						if (isProjectAccessDeniedError(error)) {
							this.toast.info("Your access changed. Refresh the project and try again.");
						} else {
							this.toast.error(message, { timeout: calculateTimeout(message) });
						}
						// heroku #21: catch the fire-and-forget refresh (see the remove-access path above).
						this.loadSharedUsers().catch((err) =>
							logError("ShareActionDialog: post-update loadSharedUsers refresh failed", err, { projectId: this.projectId })
						);
						return;
					}
				}
				this.$emit("access-changed", item, value);
				// heroku #21: catch the fire-and-forget refresh (see the remove-access path above).
				this.loadSharedUsers().catch((err) =>
					logError("ShareActionDialog: post-option-change loadSharedUsers refresh failed", err, { projectId: this.projectId })
				);
			},
			// LSP-15692: the empty-query suggested set — a small page of team members instead of the
			// whole org (the old pageSize-5000 bandaid). Typing ≥2 chars switches to server search.
			async loadTeamMembers() {
				const myGeneration = this.loadGeneration;
				this.initialTeamMembers = [];
				const result = await api.getUsers({ pageSize: SUGGESTED_DIRECTORY_PAGE_SIZE, page: 1, order: DIRECTORY_USER_ORDER });
				if (myGeneration !== this.loadGeneration) return;
				this.initialTeamMembers = result.rows.filter((user) => !!user.Account);
			},
			// LSP-15692: the empty-query suggested set — the 20 most recent contacts instead of up to
			// 50,000 (the old Dialogs.getCustomers ceiling). Typing ≥2 chars switches to server search.
			async loadCustomers() {
				const myGeneration = this.loadGeneration;
				// PR #3838 review (low #17): clear BEFORE awaiting, mirroring loadTeamMembers — otherwise
				// a re-open briefly renders the previous project's suggested contacts.
				this.initialCustomers = [];
				const result = await api.getCustomers({
					pageSize: SUGGESTED_DIRECTORY_PAGE_SIZE,
					order: [["createdAt", "DESC"]]
				});
				if (myGeneration !== this.loadGeneration) return;
				this.initialCustomers = result.rows;
			},
			async loadOrganizationAdministrators() {
				const myGeneration = this.loadGeneration;
				let sharingUsers;
				try {
					sharingUsers = await api.getSharingUsers();
					if (myGeneration !== this.loadGeneration) return;
				} catch (error) {
					logError("ShareActionDialog: first administrator roster load failed", error, { projectId: this.projectId });
					if (myGeneration !== this.loadGeneration) throw error;
					sharingUsers = await api.getSharingUsers();
					if (myGeneration !== this.loadGeneration) return;
				}

				const callerOrganizationId = this.me?.organization?.id;
				const projectOrganizationId = this.project?.organizationId;
				const includeOrganizationAdministrators =
					!callerOrganizationId || !projectOrganizationId || projectOrganizationId === callerOrganizationId;
				const organizationAdministrators = includeOrganizationAdministrators
					? sharingUsers.filter((user) =>
							user.Accounts?.[0]?.Roles?.some((role) => role?.name?.toLowerCase() === "administrator")
						)
					: [];
				this.organizationAdministratorUserIds = new Set(organizationAdministrators.map((user) => user.id));
				this.organizationAdministratorAccountIds = new Set(
					organizationAdministrators.map((user) => user.Accounts?.[0]?.id).filter(Boolean)
				);
				this.organizationAdministratorEmails = new Set(
					organizationAdministrators.map((user) => emailComparisonKey(user.email)).filter(Boolean)
				);
			},
			// LSP-15692: every keystroke from the invite field lands here; the debounced handler
			// fans out to the server. Queries under the minimum length revert to the suggested set
			// immediately (no debounce) so clearing the input snaps back without a wait.
			handleInviteQueryChange(query) {
				const trimmed = (query || "").trim();
				// Break-it round 3: a new keystroke is a new intent even when it restores an earlier
				// string, so bump BEFORE dispatching either branch.
				//
				// PR #3838 review low: "restores an earlier string" means an earlier string that is no
				// longer current — an ABA round trip, which the generation exists to catch. A keystroke
				// that leaves the TRIMMED query byte-identical to the one a search is ALREADY running for
				// (typing then deleting a trailing space, or any edit inside surrounding whitespace) is
				// not a new intent at all: bumping discarded a request that was fetching exactly the
				// right thing, and re-arming the debounce then refetched it.
				//
				// Gated on directorySearching, not on the string alone, so this can only ever adopt a
				// LIVE request. Once a search has settled — or FAILED — directorySearching is false and
				// the identical string falls through to a normal bump and dispatch, so retyping is still
				// a retry. The under-minimum branch below is unreachable here (trimmed is >= MIN), so no
				// transition into or out of it can be swallowed.
				if (trimmed === this.directoryQuery && trimmed.length >= MIN_DIRECTORY_SEARCH_LENGTH && this.directorySearching) {
					return;
				}
				this.directoryQueryGeneration += 1;
				if (trimmed.length < MIN_DIRECTORY_SEARCH_LENGTH || isSearchTooLong(query)) {
					// Clearing directoryQuery also invalidates any debounce timer already queued —
					// runDirectorySearch rechecks it at entry (break-it round 1).
					// AI-review finding 2 (587ac43c30): abandoning the query is a teardown, so disarm like
					// close()/init()/beforeUnmount() do. An abort alone does not settle a transport that
					// ignores the signal, which left a 20s watchdog scheduled against an abandoned query.
					this.directoryQuery = "";
					this.clearDirectorySearchTimeout();
					this.debouncedDirectorySearch.cancel();
					this.directorySearchScope.abortCurrent();
					this.directoryQueryActive = false;
					this.directorySearching = false;
					this.directorySearchFailed = false;
					return;
				}
				this.directoryQuery = trimmed;
				this.directorySearching = true;
				// AI-review finding 4 (587ac43c30): clear the PREVIOUS query's failure synchronously with
				// the keystroke. Clearing it only at runDirectorySearch's start left the old "we couldn't
				// finish searching" banner rendering over stale results for the whole 250ms debounce
				// window — a warning about a search that had not been attempted yet.
				this.directorySearchFailed = false;
				this.debouncedDirectorySearch(trimmed, this.directoryQueryGeneration);
			},
			// Mirrors api.searchCustomers's word-splitting: every word must match at least one of the
			// user's name/email/phone fields (case-insensitive substring).
			buildUserSearchWhere(query) {
				// The original per-word predicate: every word must match at least one column.
				const perWordClauses = textSearchPatterns(query).map((pattern) => ({
					$or: [
						{ firstname: { $ilike: pattern } },
						{ lastname: { $ilike: pattern } },
						{ email: { $ilike: pattern } },
						{ phone: { $ilike: pattern } }
					]
				}));
				// Bugbot HIGH / AI-review finding 1: a phone-shaped query is ALSO matched on normalized
				// digits as ONE term. Users.phone is free-form (no hook, no validator in contact-api's
				// user model), so the digit patterns are OR-ed with the renderings a human actually types.
				//
				// ADDITIVE, mirroring api.searchCustomers (AI-review finding 1 at 692b48af60): OR-ing the
				// phone path with the untouched per-word predicate keeps the result set a strict SUPERSET
				// of pre-diff behaviour, so nothing that used to match stops matching.
				//
				// SEARCH parses with the helper's default "US" on ALL THREE sites (here,
				// api.searchCustomers, and ShareInviteField's client filter) so the three predicates stay
				// behaviourally identical — break-it round 2 caught them disagreeing when only this one
				// used the org country. "US" is also the documented convention: see
				// SERVER_PHONE_VALIDATION_COUNTRY in helpers/phone.ts, which must mirror contact-api's
				// PhoneNumber model (it always validates with defaultCountry "US"). DEDUP/staging keeps
				// using the ORG country (ShareInviteField.phoneKey) — that is unchanged. A non-US org
				// pasting a NATIONAL-format number still needs the server-side digits-normalized
				// predicate noted in helpers/directoryQuery.ts; reported as a follow-up.
				const phonePatterns = phoneSearchPatterns(query);
				if (!phonePatterns) {
					return { $and: perWordClauses };
				}
				return {
					$and: [
						{
							$or: [
								...phonePatterns.digitPatterns.map((pattern) => ({ phone: { $ilike: pattern } })),
								...phonePatterns.literalPatterns.map((pattern) => ({ phone: { $ilike: pattern } })),
								{ $and: perWordClauses }
							]
						}
					]
				};
			},
			// Break-it round 1 on the phone delta: a request that never settles never reaches its own
			// finally, so its watchdog must be disarmed by whoever tears the dialog down.
			//
			// (PR #3838 review, comment-analyzer: the four lines that used to
			// head this method described `isForCurrentDirectoryQuery` — a copy/reflow artifact. They
			// now sit above that method instead.)
			clearDirectorySearchTimeout() {
				// Clears only, deliberately — see the sentinel comment in runDirectorySearch for why
				// resolving here would swallow the transport's real error (round-6 finding 5, reverted).
				for (const handle of this.directorySearchTimeoutHandles.splice(0)) {
					clearTimeout(handle);
				}
			},
			// Bugbot follow-up 2 (PR #3838): whether a settling request's query is still the one the
			// user is asking about. directoryQuery is updated synchronously per keystroke, so this
			// catches the window where a newer query has been typed but its debounce (and therefore
			// the startRequest that would abort/supersede this request) hasn't fired yet.
			isForCurrentDirectoryQuery(query, generation) {
				// Break-it round 3: generation defaults to the current one so a direct call (tests, or a
				// future non-debounced caller) still compares on the string alone, while the debounced
				// path — the only one that can be raced by an ABA round trip — compares on both.
				const intendedGeneration = generation ?? this.directoryQueryGeneration;
				return query === this.directoryQuery && intendedGeneration === this.directoryQueryGeneration;
			},
			async runDirectorySearch(query, generation) {
				// PR #3838 review (medium #4): normalize ONCE. Previously directoryQuery was set to the
				// trimmed value while the API calls and the currency comparisons used the RAW one, so an
				// untrimmed call searched a different string than the one recorded as current, discarded
				// its own results, and skipped the spinner clear — latching "Checking…" forever.
				const trimmedQuery = (query || "").trim();
				// UNREACHABLE TODAY — verified, and recorded rather than logged (a diagnostic on a path
				// that cannot execute is untestable noise). It is a bare return with no `finally`, so if it
				// ever DID fire while directorySearching were true, the spinner would latch with no
				// recovery. Three things keep it unreachable, and a change to ANY of them makes it live:
				//   1. this method has exactly ONE production caller — the debouncedDirectorySearch
				//      wrapper — and that wrapper only proceeds when `query === this.directoryQuery`;
				//   2. the wrapper is only ever invoked from handleInviteQueryChange's >= MIN branch, so
				//      `query` is already a trimmed string of at least MIN_DIRECTORY_SEARCH_LENGTH;
				//   3. every OTHER writer of directoryQuery sets it to "" and resets directorySearching in
				//      the same breath — the under-min branch, close() and init(). NOT beforeUnmount()
				//      (PR #3838 review, comment-analyzer: this list used to name
				//      it, which was false). beforeUnmount clears directoryQuery, bumps the generation,
				//      disarms the watchdog and aborts in flight, but deliberately leaves
				//      directorySearching alone: the component is being destroyed, so a spinner flag on a
				//      dead instance cannot latch anything a user could see. That makes it harmless here
				//      but NOT an example of the invariant, and precondition 3 rests on the other three.
				// Preconditions 1 and 2 are pinned by existing tests ("does not call either directory
				// endpoint for a one-character query", "cancels a queued debounce when the query becomes
				// whitespace-only", "does not let a debounce fire after the query was explicitly cleared").
				// If a future caller reaches this method directly, give this branch the same
				// spinner-clearing + logging treatment the other exits already have.
				if (trimmedQuery.length < MIN_DIRECTORY_SEARCH_LENGTH || isSearchTooLong(query)) {
					return;
				}
				// Break-it round 3: pin the intent this request serves for its whole lifetime.
				const intentGeneration = generation ?? this.directoryQueryGeneration;
				// Starting a search FOR a query makes it the current intent (a no-op on the debounced
				// path, which already verified query === directoryQuery at fire time). The settle-time
				// isForCurrentDirectoryQuery rechecks below still catch intent changing mid-flight.
				this.directoryQuery = trimmedQuery;
				// PR #3838 review (low #14): drop the previous attempt's failure up front so a retry
				// renders "checking" rather than keeping the stale failure card while it is in flight.
				this.directorySearchFailed = false;
				// startRequest aborts the prior in-flight pair, so rapid typing never races: only the
				// newest request's results are applied (isCurrent below), including on error paths.
				const requestState = this.directorySearchScope.startRequest();
				// AI-review finding 3: bound the fan-out. Aborting the shared controller settles both
				// halves; `timedOut` marks it as a real, user-visible failure rather than a silent
				// supersession, and the finally disarms the timer on every exit path.
				let timedOut = false;
				let timeoutHandle;
				// RACED, not merely aborted: aborting the shared controller only settles a transport
				// that HONOURS the signal. The hang this guards against is a transport that does not
				// (dropped socket, hung dyno) — for that, `await Promise.allSettled(...)` never
				// resolves and the finally never runs. Racing against a resolving sentinel guarantees
				// the UI is released; the abort still cancels the request where it is respected.
				// AI-review finding 2 (692b48af60) asked for a superseded request's watchdog to be disarmed.
				// Deliberately NOT done: an abort does not settle a transport that ignores it, so this
				// sentinel is the ONLY thing that ever settles such a request. Disarming it alone would
				// leave the promise pending forever instead of running its finally (an existing test pins
				// that it settles). The finding's real cost — telemetry noise — is addressed by logging a
				// superseded timeout at WARN instead of ERROR below.
				//
				// Round-6 finding 5 asked for teardown to also SETTLE this race (it currently only clears the
				// timer, so for a transport that ignores abort the promise stays pending). Tried, and
				// REVERTED — it makes diagnostics strictly worse. Resolving the sentinel on teardown settles
				// the race immediately, so the code returns down the timeout-discard path and the transport's
				// REAL error, when it finally arrives, is swallowed: nothing is awaiting it any more. Leaving
				// the sentinel armed means a transport that eventually rejects still reaches the
				// genuine-failure discard log with its actual root cause, which three existing tests pin
				// ("close-then-reopen … logs both late non-abort failures", "beforeUnmount aborts both calls
				// and logs both real failures when transports ignore cancellation", "unmount aborts an
				// in-flight pair and late genuine failures are only logged as discarded"). The residual is a
				// promise pending for a transport that never settles at all — in which case the underlying
				// request is leaked regardless, and no telemetry was ever available to lose.
				const timeoutSentinel = new Promise((resolve) => {
					timeoutHandle = setTimeout(() => {
						timedOut = true;
						requestState.controller.abort();
						resolve(null);
					}, DIRECTORY_SEARCH_TIMEOUT_MS);
				});
				this.directorySearchTimeoutHandles.push(timeoutHandle);
				try {
					// PR #3838 review (medium #9): allSettled, not all — with Promise.all only the
					// FIRST-settling rejection is observable, so when both sources fail the second root
					// cause is lost. Both outcomes are inspected and logged individually below.
					const settled = await Promise.race([
						Promise.allSettled([
							api.searchCustomers(trimmedQuery, { signal: requestState.signal }, DIRECTORY_SEARCH_PAGE_SIZE),
							api.getUsers(
								{
									where: this.buildUserSearchWhere(trimmedQuery),
									pageSize: DIRECTORY_SEARCH_PAGE_SIZE,
									page: 1,
									order: DIRECTORY_USER_ORDER
								},
								undefined,
								{ signal: requestState.signal }
							)
						]),
						timeoutSentinel
					]);
					// A timeout is NOT a supersession: it must surface even though the abort makes the
					// request look stale to isCurrent(). Checked before the generic failure handling.
					if (timedOut || !settled) {
						const timeoutError = new Error(`Directory search timed out after ${DIRECTORY_SEARCH_TIMEOUT_MS}ms`);
						const timeoutContext = {
							projectId: this.projectId,
							query: trimmedQuery,
							currentQuery: this.directoryQuery,
							timeoutMs: DIRECTORY_SEARCH_TIMEOUT_MS
						};
						if (this.dialog && this.isForCurrentDirectoryQuery(trimmedQuery, intentGeneration)) {
							logError("ShareActionDialog: directory search timed out", timeoutError, timeoutContext);
							this.directorySearchFailed = true;
							const timeoutMessage = "Searching your team and contacts took too long. Please try again.";
							this.toast.error(timeoutMessage, { timeout: calculateTimeout(timeoutMessage) });
							return;
						}
						// AI-review finding 1 (587ac43c30): a timeout whose query has already moved on used
						// to return with ZERO output — the only discard path here producing no telemetry,
						// while its genuine-failure sibling below logs every discard with a discardReason.
						// A 20s backend hang is the event most worth recording, and it is most likely to be
						// masked exactly here, by ordinary continued typing. Log it the same way; no toast,
						// because the user has already moved on (telemetry, not UI noise).
						// A WARNING, not an error (AI-review finding 2 at 692b48af60): the hang is still worth
						// recording — that was finding 1 of the review at 587ac43c30 — but nobody waited on
						// it, so it must not page anyone or bury the surfaced timeouts above.
						logWarn("ShareActionDialog: directory search timed out but was discarded before it could be surfaced", {
							...timeoutContext,
							error: String(timeoutError),
							discardReason: !this.dialog ? "dialog-closed" : "outdated-query"
						});
						return;
					}
					const [customerOutcome, userOutcome] = settled;
					const failures = [
						{ source: "searchCustomers", outcome: customerOutcome },
						{ source: "getUsers", outcome: userOutcome }
					].filter((entry) => entry.outcome.status === "rejected");
					// A request is only still relevant if it is the newest un-aborted one AND the user is
					// still asking about this query (a newer >=2-char query doesn't abort this one until ITS
					// debounce fires, so scope-currency alone is not enough).
					const isStale =
						!this.directorySearchScope.isCurrent(requestState.requestId) ||
						!this.isForCurrentDirectoryQuery(trimmedQuery, intentGeneration);

					if (failures.length) {
						// Break-it round 1: partition, don't just test failures[0]. A fan-out can mix an
						// honored cancellation with a transport that ignored it, and logging failures[0]
						// let an AbortError MASK the genuine failure sitting next to it.
						const genuineFailures = failures.filter((entry) => !isAbortError(entry.outcome.reason));
						if (!genuineFailures.length) {
							// Superseded by a newer query, or the query was cleared / the dialog closed —
							// never user-visible, and nothing to diagnose.
							return;
						}
						// PR #3838 review (medium #5 + medium #10): a GENUINE failure whose query has moved
						// on (the common case — users keep typing) used to return before any logging at all.
						// Per the repo's "Diagnostic logging when fixing silent failures" rule, log at EVERY
						// discard condition, with the identifiers needed to correlate: which condition
						// discarded it, the query, the project, and WHICH of the two parallel calls failed.
						if (isStale || !this.dialog) {
							const discardReason = !this.dialog
								? "dialog-closed"
								: !this.directorySearchScope.isCurrent(requestState.requestId)
									? "superseded-request"
									: "outdated-query";
							// Break-it round 1: one log PER genuine failure, each carrying its own error —
							// a single log of failures[0] lost the second root cause (and could report an
							// AbortError instead of the real one).
							genuineFailures.forEach((entry) => {
								logError(
									"ShareActionDialog: directory search failed but was discarded before it could be surfaced",
									entry.outcome.reason,
									{
										projectId: this.projectId,
										query: trimmedQuery,
										currentQuery: this.directoryQuery,
										failedSources: entry.source,
										discardReason
									}
								);
							});
							return;
						}
						genuineFailures.forEach((entry) => {
							logError("ShareActionDialog: directory search failed", entry.outcome.reason, {
								projectId: this.projectId,
								query: trimmedQuery,
								failedSources: entry.source
							});
						});
						// Mark the failure so the invite field keeps treating the query as UNRESOLVED —
						// the slice was never updated, so slice-absence proves nothing. A PARTIAL failure
						// counts: half a directory is not a resolved directory.
						this.directorySearchFailed = true;
						const message = "We couldn't search your team and contacts. Please try again.";
						this.toast.error(message, { timeout: calculateTimeout(message) });
						return;
					}

					if (isStale) {
						return;
					}
					this.searchedCustomers = customerOutcome.value.rows;
					this.searchedTeamMembers = userOutcome.value.rows.filter((user) => !!user.Account);
					this.directoryQueryActive = true;
					this.directorySearchFailed = false;
				} catch (error) {
					// allSettled never rejects, so reaching here means something OUTSIDE the fan-out threw
					// (e.g. a synchronous failure building the request). Log it rather than letting it
					// escape as an unhandled rejection from the debounce timer.
					if (isAbortError(error)) {
						return;
					}
					logError("ShareActionDialog: directory search threw outside the request fan-out", error, {
						projectId: this.projectId,
						query: trimmedQuery
					});
					if (
						!this.directorySearchScope.isCurrent(requestState.requestId) ||
						!this.dialog ||
						!this.isForCurrentDirectoryQuery(trimmedQuery, intentGeneration)
					) {
						return;
					}
					this.directorySearchFailed = true;
					// AI-review finding 5 (587ac43c30): same failure class as a fan-out rejection, so give
					// it the same feedback. This branch latched the failure card but stayed silent, while an
					// ordinary network failure of the same search toasts.
					const unexpectedMessage = "We couldn't search your team and contacts. Please try again.";
					this.toast.error(unexpectedMessage, { timeout: calculateTimeout(unexpectedMessage) });
				} finally {
					clearTimeout(timeoutHandle);
					const trackedIndex = this.directorySearchTimeoutHandles.indexOf(timeoutHandle);
					if (trackedIndex !== -1) {
						this.directorySearchTimeoutHandles.splice(trackedIndex, 1);
					}
					// Clear the spinner unless a strictly newer request took over (it owns the flag)
					// OR the user has already typed a different query whose debounce is still pending
					// (that query's request owns the flag when it settles — Bugbot follow-up 2: an
					// old request settling must not stop the "checking" state of the newer query).
					if (
						!this.directorySearchScope.isSuperseded(requestState.requestId) &&
						this.isForCurrentDirectoryQuery(trimmedQuery, intentGeneration)
					) {
						this.directorySearching = false;
					}
				}
			},
			async loadSharedUsers() {
				// Capture the in-flight generation so a stale fetch can't overwrite this.sharedUsers.
				const myGeneration = this.loadGeneration;
				this.derivedAccessSummaryLoading = true;
				const derivedAccessSummaryRequest = api
					.getProjectAccessSummary(this.projectId)
					.then((derivedAccessSummary) => {
						if (myGeneration !== this.loadGeneration) return;
						this.derivedAccessSummary = derivedAccessSummary;
					})
					.catch((error) => {
						logWarn("ShareActionDialog: derived project access summary could not be loaded", {
							projectId: this.projectId,
							error: String(error)
						});
						if (myGeneration !== this.loadGeneration) return;
						this.derivedAccessSummary = null;
					})
					.finally(() => {
						if (myGeneration !== this.loadGeneration) return;
						this.derivedAccessSummaryLoading = false;
					});
				const projectShares = await api.getProjectShares({ where: { projectId: this.projectId }, pageSize: 5000 });
				if (myGeneration !== this.loadGeneration) return;
				this.rosterCreator = projectShares?.creator ?? null;
				const sorted = await this.sortUsers(projectShares.rows.filter((user) => !!user));
				if (myGeneration !== this.loadGeneration) return;
				// Applied right before the write, where nothing can interleave: a removal can land while sortUsers awaits,
				// and a successful delete can precede server roster convergence.
				this.sharedUsers = sorted.filter((user) => {
					if (this.getTitle(user).startsWith(DELETED_USER)) return false;
					if (!this.removedShareIds.has(user.id)) return true;
					logWarn("ShareActionDialog: roster refresh returned a removed share", {
						projectId: this.projectId,
						shareId: user.id
					});
					return false;
				});
				this.peopleLoading = false;
				await derivedAccessSummaryRequest;
			},
			mapUsersWithAccounts(users, alreadySharedUsers) {
				return users
					.map((user) => {
						const alreadySharedUser = alreadySharedUsers.find((u) =>
							shareRowMatchesIdentity(user.userIdentifier, [u.email, ...(u.Accounts ?? []).map((account) => account.id)])
						);
						if (!alreadySharedUser) {
							if (!user.userIdentifier) {
								if (user.userIdentifierType === "email" || user.userIdentifierType === "phone") {
									return { ...user, isPendingInvite: true };
								}
								return user.userIdentifierType === "customer" || user.userIdentifierType === "account" ? null : user;
							}
							// LSP-17773: a raw EMAIL/PHONE invite that resolves to no registered
							// account or contact is still real (pending) access — keep it as a
							// pending-invite row built from the share itself instead of dropping it
							// (the "only the creator shows" bug). Its badges/Resend come from the
							// share row's own claim/delivery fields via claimStateOf/rowBadges.
							if (user.userIdentifierType === "email" || user.userIdentifierType === "phone") {
								return { ...user, isPendingInvite: true };
							}
							return null;
						}
						return {
							...user,
							User: alreadySharedUser,
							Account: alreadySharedUser?.Account,
							name: alreadySharedUser?.firstname + " " + alreadySharedUser?.lastname
						};
					})
					.filter((u) => !!u);
			},
			mapCustomersWithAccounts(customers, alreadySharedUsers, emailCustomers) {
				return [...customers, ...emailCustomers]
					.map((customer) => {
						const alreadySharedUser = alreadySharedUsers.find((u) =>
							shareRowMatchesIdentity(customer.userIdentifier, [u.id, u.EmailAddresses?.[0]?.emailAddress])
						);
						if (!alreadySharedUser) {
							return null;
						}
						return {
							...customer,
							Account: alreadySharedUser?.Account
						};
					})
					.filter((c) => !!c);
			},
			buildSyntheticCreatorRow() {
				const accountId = this.rosterCreator?.accountId ?? this.createdByUser?.Account?.id;
				const email = this.rosterCreator?.email ?? this.createdByUser?.User?.email;
				const userId = this.rosterCreator?.userId ?? this.createdByUser?.User?.id ?? this.createdByUserId;
				const firstname = this.rosterCreator?.firstname ?? this.createdByUser?.User?.firstname;
				const lastname = this.rosterCreator?.lastname ?? this.createdByUser?.User?.lastname;
				const name = this.createdByUser?.User?.name || [firstname, lastname].filter(Boolean).join(" ").trim();
				if ((!accountId && !email) || (!email && !name)) {
					logInfo("ShareActionDialog: creator metadata is insufficient to render a creator row", {
						projectId: this.projectId,
						creatorAccountKnown: !!accountId,
						creatorEmailKnown: !!email,
						creatorNameKnown: !!name
					});
					return null;
				}
				// Client display metadata only: no ProjectShares record exists for this row.
				const creatorUser = { id: userId, email, name, firstname, lastname };
				if (!this.createdByUser?.Account && accountId) {
					this.createdByUser = { User: creatorUser, Account: { id: accountId } };
				}
				logInfo("ShareActionDialog: rendering project creator from project metadata", { projectId: this.projectId });
				return {
					id: SYNTHETIC_CREATOR_ROW_ID,
					isSyntheticCreator: true,
					projectId: this.projectId,
					userIdentifier: accountId ?? email,
					userIdentifierType: accountId ? "account" : "email",
					accessType: AccessType.Editor,
					claimState: "claimed",
					User: creatorUser,
					Account: accountId ? { id: accountId } : undefined
				};
			},
			async sortUsers(users) {
				const myGeneration = this.loadGeneration;
				const sharingUsers = await api.getSharingUsersForProject(this.projectId);
				// Everything below is synchronous and reads/writes live creator state (rosterCreator,
				// createdByUser), so a superseded load must stop here rather than label or lock the
				// wrong row as Creator for the next project. The caller re-checks and discards the result.
				if (myGeneration !== this.loadGeneration) return [];
				this.alreadySharedUsers = sharingUsers;
				const alreadySharedUsers = this.alreadySharedUsers.filter((u) => !!u.Account);
				const emailCustomers = this.alreadySharedUsers.filter((u) => !u.Account);
				let customers = users.filter((u) => u.userIdentifierType === "customer");
				const customersFromEmail = users.filter((u) => {
					return emailCustomers.some((customer) =>
						shareRowMatchesIdentity(u.userIdentifier, [customer.EmailAddresses?.[0]?.emailAddress])
					);
				});
				const originalShares = users;
				users = this.mapUsersWithAccounts(users, alreadySharedUsers);
				customers = this.mapCustomersWithAccounts(customers, this.alreadySharedUsers, customersFromEmail);
				this.createdByUser = users.find((user) => user.User?.id == this.createdByUserId) ?? this.createdByUser;
				if (this.rosterCreator) {
					const creatorUser = this.createdByUser?.User;
					const firstname = this.rosterCreator.firstname ?? creatorUser?.firstname;
					const lastname = this.rosterCreator.lastname ?? creatorUser?.lastname;
					this.createdByUser = {
						...this.createdByUser,
						User: {
							...creatorUser,
							id: this.rosterCreator.userId ?? creatorUser?.id ?? this.createdByUserId,
							email: this.rosterCreator.email ?? creatorUser?.email,
							firstname,
							lastname,
							name: creatorUser?.name || [firstname, lastname].filter(Boolean).join(" ").trim()
						},
						Account: this.rosterCreator.accountId ? { id: this.rosterCreator.accountId } : this.createdByUser?.Account
					};
				}
				const creatorAccountId = this.createdByUser?.Account?.id;
				const creatorEmail = this.createdByUser?.User?.email;
				// The sharing-user lookup may omit the creator. Preserve their verified real grant
				// even when account mapping drops it, so metadata never produces a duplicate row.
				const creatorGrant = originalShares.find((share) =>
					shareRowMatchesIdentity(share.userIdentifier, [creatorAccountId, creatorEmail])
				);
				if (creatorGrant) {
					const creatorRowIndex = users.findIndex((share) => share.id === creatorGrant.id);
					const creatorRow = creatorRowIndex >= 0 ? users[creatorRowIndex] : creatorGrant;
					const resolvedCreatorRow = {
						...creatorRow,
						User: creatorRow.User ?? this.createdByUser?.User,
						Account: creatorRow.Account ?? this.createdByUser?.Account
					};
					if (creatorRowIndex >= 0) users[creatorRowIndex] = resolvedCreatorRow;
					else users.push(resolvedCreatorRow);
				}
				const projectCreator =
					creatorAccountId || creatorEmail
						? users.find((u) => shareRowMatchesIdentity(u.userIdentifier, [creatorAccountId, creatorEmail]))
						: undefined;
				const creatorRowById = users.find((user) => !!this.createdByUserId && user.User?.id == this.createdByUserId);
				const creatorEmailKey = creatorEmail ? emailComparisonKey(creatorEmail) : "";
				const creatorRowByEmail =
					!!creatorEmailKey &&
					[...users, ...customers].some(
						(row) =>
							emailComparisonKey(row.User?.email ?? row.Customer?.EmailAddresses?.[0]?.emailAddress ?? row.EmailAddress?.emailAddress ?? "") ===
							creatorEmailKey
					);
				const syntheticCreator = !projectCreator && !creatorRowById && !creatorRowByEmail ? this.buildSyntheticCreatorRow() : null;
				const myAccountId = this.me?.account?.id;
				const myEmail = this.me?.user?.email;
				const myUser = users.find((u) => shareRowMatchesIdentity(u.userIdentifier, [myAccountId, myEmail]));
				const usersList = projectCreator ? [projectCreator] : syntheticCreator ? [syntheticCreator] : [];
				if (myUser != null && this.me?.user?.id !== this.createdByUser?.User?.id) {
					usersList.push(myUser);
				}
				// Bugbot: an email share whose identifier is a CONTACT's email is synthesized as a
				// pending-invite row by mapUsersWithAccounts (no matching account) AND re-added as a
				// resolved customer row via customersFromEmail — the same ProjectShare id twice
				// (duplicate v-for :key, inflated "Manage access" count). Dedupe by share id, preferring
				// the resolved customer row: both rows spread the same share row, so the claim/delivery
				// badge fields (claimState/claimedAt/expiresAt/deliveryStatus) that claimStateOf and
				// rowBadges read survive the swap and the pending badge doesn't vanish.
				const rowsById = new Map();
				for (const row of [...users, ...customers]) {
					const existing = rowsById.get(row.id);
					if (!existing || existing.isPendingInvite) {
						rowsById.set(row.id, row);
					}
				}
				users = Array.from(rowsById.values());
				return [
					...usersList,
					...users
						.filter(
							(u) =>
								!shareRowMatchesIdentity(u.userIdentifier, [creatorAccountId, creatorEmail]) &&
								!shareRowMatchesIdentity(u.userIdentifier, [myAccountId, myEmail])
						)
						.sort((a, b) => {
							const nameA = this.getTitle(a);
							const nameB = this.getTitle(b);
							return nameA.localeCompare(nameB);
						})
				];
			},
			// LSP-17773 / Bugbot round 13: prefer the server-derived claimState — both share shapes
			// now carry it (/users/project/:id since LSP-17771; /contact/project-share since round
			// 12), keyed on the claimTokenHash the API strips from responses. The local expiresAt
			// derivation is a legacy-server fallback ONLY: viewer invites (and editor invites while
			// token links are off) keep their token with expiresAt null, so expiry presence no
			// longer tracks the token 1:1 and would misread those pending invites as "active".
			claimStateOf(item) {
				if (["claimed", "expired", "pending", "active"].includes(item.claimState)) {
					return item.claimState;
				}
				if (item.claimedAt) {
					return "claimed";
				}
				if (item.expiresAt && new Date(item.expiresAt).getTime() < Date.now()) {
					return "expired";
				}
				if (item.expiresAt) {
					return "pending";
				}
				return "active";
			},
			isGuestRow(item) {
				if (item?.isSyntheticCreator) return false;
				if (item.userIdentifierType !== "email" && item.userIdentifierType !== "phone") {
					return false;
				}
				const email = item.userIdentifier || item.User?.email || item.EmailAddress?.emailAddress || "";
				// Merge note (LSP-15692 + develop): develop's shareRowMatchesIdentity comparison is kept —
				// it canonicalizes emails (case/dotted-I) and requires both sides present. LSP-15692 adds
				// the second source: teamMembers is now only a small suggested/searched SLICE of the org,
				// so slice-absence alone must not label a real team member a guest; the per-project
				// sharing users (bounded, loaded with the list) are consulted as well.
				const matchesTeamEmail = (candidate) => shareRowMatchesIdentity(email, [candidate.email]);
				return (
					!this.teamMembers.some(matchesTeamEmail) &&
					!(this.alreadySharedUsers || []).some((user) => !!user.Account && matchesTeamEmail(user))
				);
			},
			rowBadges(item) {
				const badges = [];
				const state = this.claimStateOf(item);
				if (state === "pending") {
					badges.push({ text: "Invited · pending", tone: "grey" });
				} else if (state === "expired") {
					badges.push({ text: "Invite expired", tone: "warn" });
				}
				if (item.deliveryStatus === "bounced") {
					badges.push({ text: "Email bounced", tone: "error" });
				}
				// trial-ended collaborator (rendered when the row carries the signal).
				if (item.trialEnded) {
					badges.push({ text: "View only — trial ended", tone: "warn" });
				}
				if (this.isGuestRow(item)) {
					badges.push({ text: "Guest", tone: "blue" });
				}
				return badges;
			},
			canResendInvite(item) {
				if (item?.isSyntheticCreator) return false;
				const state = this.claimStateOf(item);
				return (state === "pending" || state === "expired") && !!item.id && !this.hasDerivedAdministratorAccess(item);
			},
			// LSP-18911: block EVERY resend row while any resend is in flight. The old
			// `:disabled="resendingId === item.id"` only disabled the row being sent, so a click on a
			// DIFFERENT row landed on resendInvite's `if (this.resendingId) return` guard and silently
			// did nothing — a live-looking button that ignores you. Rows also stay blocked through
			// their own post-attempt cooldown so a failing resend can't be hammered into a stack of
			// identical error toasts.
			isResendBlocked(item) {
				return this.resendingId !== null || this.resendCooldowns[item.id] === true;
			},
			resendLabel(item) {
				if (this.resendingId === item.id) {
					return "Sending…";
				}
				const outcome = this.resendOutcomes[item.id];
				if (outcome === "sent") {
					return "Invite sent";
				}
				if (outcome === "failed") {
					return "Try again";
				}
				return "Resend invite";
			},
			// Records what the attempt did and opens this row's cooldown window.
			startResendCooldown(shareId, outcome) {
				this.resendOutcomes = { ...this.resendOutcomes, [shareId]: outcome };
				this.resendCooldowns = { ...this.resendCooldowns, [shareId]: true };
				clearTimeout(this.resendCooldownTimers[shareId]);
				this.resendCooldownTimers[shareId] = setTimeout(() => {
					const remaining = { ...this.resendCooldowns };
					delete remaining[shareId];
					this.resendCooldowns = remaining;
					delete this.resendCooldownTimers[shareId];
				}, RESEND_COOLDOWN_MS);
			},
			async resendInvite(item) {
				if (item?.isSyntheticCreator) {
					logWarn("ShareActionDialog: ignoring share mutation on synthetic creator row", {
						projectId: this.projectId,
						action: "resendInvite"
					});
					return;
				}
				// Belt-and-suspenders: the button is already disabled in both of these states, but a
				// programmatic/synthetic click (or a stale render) must not slip a second request past.
				if (this.resendingId || this.resendCooldowns[item.id] === true) {
					return;
				}
				const shareId = item.id;
				this.resendingId = shareId;
				try {
					await api.resendProjectShareInvite(shareId);
					const message = "We've sent the invite again.";
					this.toast.success(message, { timeout: calculateTimeout(message) });
					this.startResendCooldown(shareId, "sent");
					await this.loadSharedUsers();
				} catch (error) {
					logError("ShareActionDialog: resendInvite failed", error, { projectId: this.projectId, shareId });
					const message = error?.message || "Something went wrong resending the invite. Please try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
					this.startResendCooldown(shareId, "failed");
				} finally {
					this.resendingId = null;
				}
			},
			async copyLink() {
				try {
					this.shareUrl = await this.getShareUrlOfProject();
					const copied = await copyToClipboard(this.shareUrl);
					if (copied) {
						const message = "The project link has been copied to the clipboard.";
						this.toast.success(message, { timeout: calculateTimeout(message) });
					} else if (DeviceUtility.isTablet || DeviceUtility.isMobile) {
						this.$emit("copy-link");
					} else {
						const message = "Something went wrong copying the project link. Please try again.";
						this.toast.error(message, { timeout: calculateTimeout(message) });
					}
				} catch (err) {
					console.error("error copying link", err);
					const message = "Something went wrong copying the project link. Please try again.";
					this.toast.error(message, { timeout: calculateTimeout(message) });
				}
			},
			// LSP-16156: name the channel(s) the selected recipients are actually notified through.
			buildShareConfirmationMessage(recipients) {
				const willEmail = recipients.some((recipient) => recipient.email);
				const willText = recipients.some((recipient) => recipient.phone);
				let channel;
				if (willEmail && willText) {
					channel = "an email and a text message";
				} else if (willEmail) {
					channel = "an email";
				} else if (willText) {
					channel = "a text message";
				} else {
					return "The project has been shared.";
				}
				const subject = recipients.length > 1 ? "The people you shared with" : "The person you shared with";
				return `The project has been shared. ${subject} will get ${channel} with a link to view it.`;
			},
			async sendStaged() {
				const identifierType = (item) => (item.phoneOnly ? "phone" : item.email ? "email" : "customer");
				const describeRecipient = (item) => item.name || item.email || item.phone;
				const describeRateLimitedRecipient = (item) => describeRecipient(item) || "this recipient";
				// Double-submit guard: a second Share click while a batch is in flight is a no-op.
				// This is the "disabled for a genuinely in-flight async submit" case (repo CLAUDE.md),
				// not a validity gate — recipients staged mid-flight are preserved by the batch
				// snapshot below and go out on the next click.
				if (this.sendingStaged) {
					return;
				}
				// LSP-17773: the Share button stays enabled (repo CLAUDE.md "Form action buttons — always
				// enabled, no-op when invalid") — no-op when the queue is empty or any recipient still
				// needs an access choice.
				//
				// LSP-19140 removed this toast, and LSP-20182 puts it back on purpose — read both before
				// touching it. LSP-19140's objection was never "don't explain an empty queue"; it was
				// that a control greyed via `.dis` which ANSWERS a click reads as a disabled control
				// talking back. LSP-20182 deleted that grey (plus `aria-disabled` and the
				// pale-until-hover base colour), so the premise is gone: a button that now looks
				// unambiguously enabled and swallows the click in silence is the remaining
				// inconsistency. The toast is also the only feedback that reaches touch and keyboard
				// users, because the `title` fallback LSP-19140 left behind is mouse-hover only.
				//
				// This explains without sending, which is LSP-20182's second acceptance criterion, and
				// matches the two guards below that also react to what the user staged. The button
				// still never gains native `disabled` (repo CLAUDE.md "Form action buttons — always
				// enabled, no-op when invalid").
				if (!this.staged.length) {
					const message = "Add people above to share this project with them.";
					this.toast.info(message, { timeout: calculateTimeout(message) });
					return;
				}
				const pending = this.staged.find((item) => !item.role);
				if (pending) {
					const message = `Choose Viewer or Editor access for ${pending.name} first.`;
					this.toast.info(message, { timeout: calculateTimeout(message) });
					return;
				}
				// The per-recipient note has a visible over-limit (red) counter in ShareStagedRow; the
				// Share button stays enabled but must no-op while any note is over the cap (repo CLAUDE.md).
				const overLimit = this.staged.find((item) => countCharacters(item.note || "") > STAGED_NOTE_MAX);
				if (overLimit) {
					const message = `Shorten the note for ${overLimit.name} (max ${STAGED_NOTE_MAX} characters) before sharing.`;
					this.toast.info(message, { timeout: calculateTimeout(message) });
					return;
				}
				// Snapshot the batch at entry: recipients staged WHILE these sends are in flight are
				// NOT part of this batch, so the resolved-row removal at the tail must not touch them —
				// they stay queued for the next Share click.
				const batch = [...this.staged];
				this.sendingStaged = true;
				try {
					const sends = [];
					// Every batch row dropped WITHOUT a create is recorded here with a human-readable
					// reason and surfaced in one merged info toast below — a staged recipient must never
					// vanish from the queue without a user-visible message.
					const skipped = [];
					// F12: two staged rows can normalize to the SAME identifier (e.g. one phone number
					// typed in two formats slipping past the stage-time dedup) — track each recipient's
					// normalized identifier so one batch never fires duplicate creates for one person.
					const batchIdentifiers = new Set();
					for (const item of batch) {
						// LSP-17773 (GAP 1): the per-recipient note is persisted on the ProjectShare row and
						// rendered into the invite email (quoted block) and SMS (curly-quote line) by
						// contact-api. An empty/whitespace note is dropped here and omitted server-side.
						const note = (item.note || "").trim() || undefined;
						if (item.phoneOnly) {
							const e164 = this.stagedPhoneE164(item);
							if (!e164) {
								skipped.push({
									item,
									reason: `We couldn't use the phone number for ${item.name} — check it and add them again.`
								});
								continue;
							}
							// Belt-and-suspenders dedup: the default country code is only applied here (at
							// send time), so a number typed WITHOUT it can reach this point matching an
							// existing share stored in E.164. usedIdentifiers holds every current share's
							// E.164 — if this number is already there, tell the sharer and skip instead of
							// creating a duplicate share.
							if (this.usedIdentifiers.includes(e164.toLowerCase())) {
								skipped.push({ item, reason: `${item.name} already has access to this project.` });
								continue;
							}
							if (batchIdentifiers.has(e164.toLowerCase())) {
								skipped.push({ item, reason: `We combined the duplicate entries for ${item.name}.` });
								continue;
							}
							batchIdentifiers.add(e164.toLowerCase());
							sends.push({
								item,
								channel: { phone: e164 },
								promise: api.createProjectShare({
									projectId: this.projectId,
									userIdentifier: e164,
									userIdentifierType: "phone",
									accessType: item.role,
									note
								})
							});
							continue;
						}
						if (item.customerId && !item.email) {
							// Existing contact without an email → customer share. Editors need an email, so a
							// non-Viewer customer share is skipped (mirrors the create-share backend contract).
							if (item.role !== AccessType.Viewer) {
								skipped.push({
									item,
									reason: `${item.name} needs an email address before they can be invited as an editor.`
								});
								continue;
							}
							const customerKey = `customer:${String(item.customerId).toLowerCase()}`;
							if (batchIdentifiers.has(customerKey)) {
								skipped.push({ item, reason: `We combined the duplicate entries for ${item.name}.` });
								continue;
							}
							batchIdentifiers.add(customerKey);
							sends.push({
								item,
								channel: {},
								promise: api.createProjectShare({
									projectId: this.projectId,
									userIdentifier: item.customerId,
									userIdentifierType: "customer",
									accessType: item.role,
									note
								})
							});
							continue;
						}
						if (item.email) {
							if (this.sharedUsers.find((shared) => shareRowMatchesIdentity(shared.userIdentifier, [item.email]))) {
								skipped.push({ item, reason: `${item.name} already has access to this project.` });
								continue;
							}
							const emailKey = emailComparisonKey(item.email);
							if (batchIdentifiers.has(emailKey)) {
								skipped.push({ item, reason: `We combined the duplicate entries for ${item.name}.` });
								continue;
							}
							batchIdentifiers.add(emailKey);
							// Push AFTER the already-shared skip so the sends mirror real creates — the
							// confirmation message must not promise an email to someone who got no invite.
							sends.push({
								item,
								channel: { email: item.email },
								promise: api.createProjectShare({
									projectId: this.projectId,
									userIdentifier: item.email,
									userIdentifierType: "email",
									accessType: item.role,
									note
								})
							});
							continue;
						}
						// No phone, no customer id, no email — there is nothing to send an invite to.
						// Record it so the row doesn't silently vanish from the queue.
						skipped.push({
							item,
							reason: `We couldn't share with ${item.name} — they have no email or phone number to invite.`
						});
					}
					const skippedItems = new Set(skipped.map(({ item: skippedItem }) => skippedItem));
					// Every staged recipient was skipped (already has access, an editor customer-share
					// with no email, a duplicate, or an unusable phone number) — nothing was created.
					// Don't report success; remove the skipped rows (a blind retry can't fix their
					// reasons) and name each one, leaving anything staged mid-click untouched.
					if (!sends.length) {
						this.staged = this.staged.filter((item) => !skippedItems.has(item));
						const info = skipped.map(({ reason }) => reason).join(" ");
						this.toast.info(info, { timeout: calculateTimeout(info) });
						return;
					}
					// F16: settle every create individually — one failed recipient must not hide the
					// outcome of the others, and succeeded rows must leave the queue so a retry can't
					// send them a duplicate invite.
					const results = await Promise.allSettled(sends.map((send) => send.promise));
					const stillStagedItems = new Set(this.staged);
					const succeeded = [];
					const alreadyShared = [];
					const rateLimited = [];
					const failed = [];
					results.forEach((result, index) => {
						const send = sends[index];
						const status = result.status === "rejected" ? getApiErrorStatus(result.reason) : undefined;
						if (result.status === "fulfilled") {
							succeeded.push(send);
						} else if (status !== 429 && result.reason?.message?.includes(ALREADY_SHARED_MESSAGE)) {
							// Preserve the pre-allSettled special case: "already been shared" is
							// informational, not a failure — the recipient has access either way.
							alreadyShared.push({ send, reason: result.reason });
						} else if (status === 429) {
							rateLimited.push({ send, reason: result.reason });
						} else {
							failed.push({ send, reason: result.reason });
						}
					});
					// Remove ONLY this batch's resolved rows — fulfilled, already-shared, and skipped
					// recipients leave the queue. Genuinely-failed rows stay staged (with each
					// recipient's role + note) so the sharer can retry them, and recipients staged
					// while the batch was in flight are not part of any of these sets, so they survive.
					const resolvedItems = new Set([
						...succeeded.map((send) => send.item),
						...alreadyShared.map(({ send }) => send.item),
						...skippedItems
					]);
					const rateLimitedItems = new Set(rateLimited.map(({ send }) => send.item));
					this.staged = this.staged
						.filter((item) => !resolvedItems.has(item))
						.map((item) => ({ ...item, rateLimited: rateLimitedItems.has(item) }));
					if (succeeded.length) {
						const message = this.buildShareConfirmationMessage(succeeded.map((send) => send.channel));
						this.toast.success(message, { timeout: calculateTimeout(message) });
					}
					// Merge the local skip reasons with the server-detected "already been shared"
					// rejections so one batch produces at most ONE info toast, naming every recipient
					// it covers — no one drops out of the queue without a message.
					const infoParts = [...skipped.map(({ reason }) => reason), ...alreadyShared.map(({ reason }) => reason.message)];
					if (infoParts.length) {
						const info = infoParts.join(" ");
						this.toast.info(info, { timeout: calculateTimeout(info) });
					}
					if (rateLimited.length) {
						rateLimited.forEach(({ send, reason }) =>
							logError("ShareActionDialog: createProjectShare rate-limited", reason, {
								projectId: this.projectId,
								userIdentifierType: identifierType(send.item)
							})
						);
					}
					const stillStagedRateLimited = rateLimited.filter(({ send }) => stillStagedItems.has(send.item));
					if (stillStagedRateLimited.length) {
						const displayedRateLimitedNames = stillStagedRateLimited
							.slice(0, MAX_RATE_LIMIT_TOAST_RECIPIENTS)
							.map(({ send }) => describeRateLimitedRecipient(send.item))
							.join(", ");
						const remainingRateLimitedCount = stillStagedRateLimited.length - MAX_RATE_LIMIT_TOAST_RECIPIENTS;
						const rateLimitedNames =
							remainingRateLimitedCount > 0
								? `${displayedRateLimitedNames} and ${remainingRateLimitedCount} more`
								: displayedRateLimitedNames;
						const retryAfterSeconds = Math.max(
							...stillStagedRateLimited.map(({ send, reason }) => {
								const candidate = reason?.retryAfter;
								if (typeof candidate === "number" && Number.isFinite(candidate) && candidate > 0) {
									if (candidate <= MAX_RATE_LIMIT_RETRY_AFTER_SECONDS) {
										return candidate;
									}
									logWarn(
										"ShareActionDialog: rate-limit retryAfter exceeds the sanity cap; using the cap instead of overstating confidence in a short wait",
										{
											projectId: this.projectId,
											retryAfter: candidate,
											stagedRecipientId: send.item.id,
											userIdentifierType: identifierType(send.item)
										}
									);
									return MAX_RATE_LIMIT_RETRY_AFTER_SECONDS;
								}
								logWarn("ShareActionDialog: rate-limit response has invalid retryAfter; using default", {
									projectId: this.projectId,
									retryAfter: reason?.retryAfter,
									stagedRecipientId: send.item.id,
									userIdentifierType: identifierType(send.item)
								});
								return RATE_LIMIT_FALLBACK_RETRY_AFTER_SECONDS;
							})
						);
						const reasonText = getMessageFromError(stillStagedRateLimited[0].reason, RATE_LIMIT_FALLBACK_REASON);
						const message = `${reasonText} You can share with ${rateLimitedNames} in about ${formatDelayHuman(retryAfterSeconds)}.`;
						this.toast.warning(message, { timeout: calculateTimeout(message) });
					}
					if (failed.length) {
						failed.forEach(({ send, reason }) =>
							logError("ShareActionDialog: createProjectShare failed", reason, {
								projectId: this.projectId,
								userIdentifierType: identifierType(send.item)
							})
						);
						const failedNames = failed.map(({ send }) => describeRecipient(send.item)).join(", ");
						const message = `We couldn't share this project with ${failedNames}. Please try again.`;
						this.toast.error(message, { timeout: calculateTimeout(message) });
					}
					// LSP-17773: keep the dialog OPEN after sharing so the sharer can see the
					// newly-added people in the access list and share with more — don't auto-close.
					// Refresh the list REGARDLESS of outcome so usedIdentifiers reflects any creates
					// that did land — a stale list would let a retry duplicate them.
					await this.loadSharedUsers().catch((err) =>
						logError("ShareActionDialog: post-send loadSharedUsers refresh failed", err, { projectId: this.projectId })
					);
					if (succeeded.length) {
						this.$emit("shared");
						EventBus.$emit("ProjectSharesUpdated", this.projectId);
					}
				} catch (err) {
					// Last-resort guard for unexpected synchronous failures (the per-recipient creates
					// are settled individually above and can no longer reject this block).
					logError("ShareActionDialog: sendStaged failed", err, { projectId: this.projectId });
					this.toast.error(err.message || ERROR_MESSAGE, { timeout: calculateTimeout(err.message || ERROR_MESSAGE) });
				} finally {
					// Always release the in-flight latch — including the skipped-only early return and
					// the unexpected-failure path — or Share would be a permanent no-op.
					this.sendingStaged = false;
				}
			}
		}
	};
</script>

<!--
	Ported verbatim from design-ref/lp-real.css (.rs-*, .r-menu). Intentionally NOT scoped: the
	dialog is teleported and its icons are injected via v-html (SVG strings without a scoped
	data-v attribute), so scoped selectors would not reach them. Every selector is namespaced under
	.rs-root, which only ever exists on this dialog's teleported overlay — effectively scoping it.
-->
<style>
	.rs-root {
		--blue: #2563eb;
		--ink: #111827;
		--ink2: #374151;
		--ink3: #6b7280;
		--ink4: #9ca3af;
		--line: #d5dae2;
		--line2: #eceff3;
		/* LSP-18910: this dialog used to hard-code the OS system font, so every string inside it —
		   including the "People With Access To Project" and "Share With Others" headings — rendered
		   in Segoe UI / San Francisco while the rest of the product renders in Inter (body) and
		   Inter Tight (headings), loaded in index.html. Match the product stack. */
		font-family:
			Inter,
			-apple-system,
			BlinkMacSystemFont,
			"Segoe UI",
			system-ui,
			sans-serif;
		color: var(--ink);
	}
	/* Bugbot: Vuetify layout items (v-app-bar, v-bottom-navigation) carry inline z-index ~1000+,
	   and the v-dialog this overlay replaced rendered at Vuetify's 2000 overlay tier. At the old
	   400/410 the scrim painted UNDER the header/bottom-nav (still visible AND clickable) and the
	   card could clip behind the app bar. Keep dim < dialog. The create-contact form is now the
	   shared CreateCustomerDialog (a separate v-dialog Vuetify stacks above this one). */
	.rs-root .rs-dim {
		position: fixed;
		inset: 0;
		background: rgba(17, 24, 39, 0.5);
		z-index: 2000;
	}
	.rs-root .rs-dlg {
		position: fixed;
		z-index: 2010;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 660px;
		max-width: 94vw;
		max-height: 88vh;
		overflow-y: auto;
		background: #fff;
		border-radius: 20px;
		padding: 0 36px;
		box-shadow: 0 30px 70px -20px rgba(0, 0, 0, 0.4);
	}
	.rs-root .rs-dlg.share {
		display: flex;
		flex-direction: column;
		overflow-y: auto;
		/* LSP-19139: fixed regardless of search state — see ShareInviteField.vue's updateSuggestionsViewportCap
		   for why this number was chosen (header + field + caption + a full 18rem/6-row dropdown + footer,
		   plus a little slack). Un-conditional and independent of the query/result count on purpose: an
		   earlier version of this fix instead grew the dialog dynamically to fit however many results a
		   search returned, which looked like the whole "Share With Others" window resizing itself while the
		   admin was mid-keystroke. Reserving the room up front means the CARD never changes size — a long
		   result list scrolls inside the dropdown instead (`.rs-sugg`'s own `overflow-y: auto`), exactly as
		   it already does past that cap.
		   Capped at `88vh` to MATCH the `max-height` above, not just stay under it: per spec, when
		   min-height and max-height conflict, min-height always wins and max-height is ignored outright —
		   a bare `40rem` would force the card taller than a short viewport with `overflow: visible` giving
		   it no scroll mechanism of its own, stranding the close button and footer off-screen (Cursor
		   Bugbot, PR #4630). `min(40rem, 88vh)` keeps the two properties from ever disagreeing: on a normal
		   viewport this still resolves to a flat 40rem, and only shrinks below that on a viewport short
		   enough that 88vh itself is under 40rem — the same graceful degradation `max-height` already
		   provided pre-LSP-19139. */
		min-height: min(40rem, 88vh);
	}
	.rs-root .rs-h {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 24px;
		position: sticky;
		top: 0;
		z-index: 6;
		background: #fff;
		padding: 30px 0 2px;
		margin-top: 0;
	}
	.rs-root .rs-dlg > .rs-foot {
		position: sticky;
		bottom: 0;
		z-index: 6;
		background: #fff;
		padding: 14px 0 26px;
		margin-bottom: 0;
		box-shadow: 0 -1px 0 var(--line);
	}
	.rs-root .rs-dlg.share > .rs-foot {
		position: sticky;
		flex-shrink: 0;
		margin-top: auto;
	}
	.rs-root .rs-h h2 {
		margin: 0;
		/* Headings across the product use Inter Tight (see $heading-font-family in
		   styles/settings.scss). This declaration is belt-and-suspenders — index.html already sets
		   h1-h5 to Inter Tight globally, which is why the first LSP-18910 attempt changed nothing
		   visible: a rule matching the h2 directly already beat the face inherited from .rs-root. */
		font-family:
			"Inter Tight",
			Inter,
			-apple-system,
			BlinkMacSystemFont,
			"Segoe UI",
			system-ui,
			sans-serif;
		font-size: 24px;
		/* LSP-18910: was 800, which nothing else in contact-admin used. index.html only loads Inter
		   Tight at 100/300/400/500/700/900, and CSS weight matching resolves a desired 800 upward to
		   900 — so these titles rendered in Black, heavier than any other heading in the product.
		   700 matches the reference dialog (.conversation-card h2 in ActionStartRecording, which
		   declares no weight and so renders at the UA bold default). */
		font-weight: 700;
		letter-spacing: -0.01em;
	}
	.rs-root .rs-linkbtn {
		width: 40px;
		height: 40px;
		border-radius: 999px;
		border: 0;
		background: transparent;
		color: var(--blue);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.rs-root .rs-linkbtn:hover {
		background: #eef4ff;
	}
	.rs-root .rs-hactions {
		display: flex;
		align-items: center;
		gap: 4px;
	}
	.rs-root .rs-closex {
		width: 40px;
		height: 40px;
		border-radius: 999px;
		border: 0;
		background: transparent;
		color: var(--ink2);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.rs-root .rs-closex:hover {
		background: #f1f5f9;
		color: var(--ink);
	}
	.rs-root .rs-closex svg {
		width: 20px;
		height: 20px;
	}
	.rs-root .rs-sub {
		font-size: 18px;
		font-weight: 700;
		margin: 4px 0 16px;
	}
	.rs-root .rs-stagesec {
		margin: 0 0 22px;
		/* LSP-20183: this section used to be the flexible child between a large invite field and
		   the non-shrinking footer. In a short window flexbox collapsed it to 24px (and to 0px in
		   Android landscape), so its own scrollbar could never reveal a complete recipient. Keep
		   the staged content at a reviewable height and let the already-scrollable dialog move it
		   between the sticky header/footer instead. Multiple recipients grow the same outer scroll
		   surface; none can be trapped behind a nested, collapsed scroller. */
		flex: 0 0 auto;
		min-height: 10rem;
		overflow-y: visible;
	}
	.rs-root .rs-stagehead {
		font-size: 13px;
		font-weight: 700;
		color: var(--ink2);
		margin: 0 0 10px;
	}
	.rs-root .rs-empty {
		padding: 14px;
		font-size: 13px;
		color: var(--ink3);
		text-align: center;
	}
	/* footer */
	.rs-root .rs-foot {
		display: flex;
		justify-content: flex-end;
		gap: 22px;
		align-items: center;
		margin-top: 20px;
	}
	.rs-root .rs-foot.split {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
	.rs-root .rs-backb {
		background: none;
		border: 0;
		font-size: 15px;
		font-weight: 700;
		color: var(--ink2);
		cursor: pointer;
	}
	.rs-root .rs-backb:hover {
		color: var(--ink);
	}
	/* LSP-20182: one colour, no hover swap. The button used to sit at a pale #7fb0f9 and only
	   reach the full --blue on hover, which read as "not ready yet" until you pointed at it —
	   the same false unavailable signal as the `.dis` grey. The hover colour was the correct
	   one, so it is now the only one. */
	.rs-root .rs-shareb {
		height: 46px;
		padding: 0 28px;
		border-radius: 999px;
		background: var(--blue);
		color: #fff;
		font-weight: 700;
		font-size: 15px;
		border: 0;
		cursor: pointer;
	}
	.rs-root .rs-manageb {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		height: 38px;
		padding: 0 14px;
		border: 1px solid var(--line2);
		border-radius: 8px;
		background: #fff;
		font: inherit;
		font-size: 13.5px;
		font-weight: 600;
		color: var(--ink);
		cursor: pointer;
	}
	.rs-root .rs-manageb:hover {
		background: #f3f5f8;
	}
	.rs-root .rs-manageb svg {
		width: 17px;
		height: 17px;
		color: var(--ink2);
	}
	.rs-root .rs-mcount {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 20px;
		height: 20px;
		padding: 0 6px;
		border-radius: 999px;
		background: #eef2f7;
		color: var(--ink2);
		font-size: 12px;
		font-weight: 700;
	}
	/* manage-access header */
	.rs-root .rs-h.manage {
		display: flex;
		align-items: center;
		gap: 10px;
		/* LSP-17773: the base .rs-h is justify-content: space-between (title left, actions right).
		   The manage header is "‹ Back" + title, which must sit TOGETHER on the left (the gap:10px
		   above only makes sense with flex-start) — without this override they were flung to
		   opposite ends, stranding the Back button. */
		justify-content: flex-start;
	}
	.rs-root .rs-h.manage h2 {
		font-size: 17px;
		margin: 0;
	}
	.rs-root .rs-mback {
		display: inline-flex;
		align-items: center;
		gap: 2px;
		height: 32px;
		padding: 0 10px 0 6px;
		border: 1px solid var(--line2);
		border-radius: 8px;
		background: #fff;
		font: inherit;
		font-size: 13px;
		font-weight: 600;
		color: var(--ink2);
		cursor: pointer;
	}
	.rs-root .rs-mback:hover {
		background: #f3f5f8;
		color: var(--ink);
	}
	/* LSP-18908: the chevron arrives through v-html as an <svg> inside an inline <span>, so it sat
	   on the text baseline of that span rather than participating in .rs-mback's flex centring —
	   which read as a top-aligned icon next to the "Back" label and the search field below it.
	   Making the wrapper a flex box (and the svg a block) lets align-items: center reach the glyph. */
	.rs-root .rs-mback > span {
		display: inline-flex;
		align-items: center;
	}
	.rs-root .rs-mback svg {
		width: 16px;
		height: 16px;
		display: block;
	}
	/* search */
	.rs-root .rs-search {
		display: flex;
		align-items: center;
		gap: 10px;
		border: 1px solid #94a3b8;
		border-radius: 10px;
		height: 52px;
		padding: 0 16px;
		color: var(--ink3);
		margin-bottom: 8px;
	}
	.rs-root .rs-search input {
		border: 0;
		outline: 0;
		font: inherit;
		font-size: 15.5px;
		color: var(--ink);
		flex: 1;
		background: transparent;
	}
	/* people rows */
	.rs-root .rs-row {
		display: flex;
		align-items: center;
		gap: 14px;
		padding: 13px 0;
		position: relative;
	}
	.rs-root .rs-meta {
		flex: 1;
		min-width: 0;
	}
	.rs-root .rs-nm {
		font-size: 15.5px;
		font-weight: 700;
		/* Same unbreakable-token problem as ShareStagedRow's .sn/.se: these rows render an email as
		   the name when there is no display name, and Manage Access is the sibling of the staged row
		   the overflow was reported on. */
		overflow-wrap: anywhere;
	}
	.rs-root .rs-sub2 {
		font-size: 13px;
		color: var(--ink3);
		margin-top: 3px;
		overflow-wrap: anywhere;
	}
	.rs-root .rs-badges {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		margin-top: 6px;
	}
	.rs-root .rs-badge {
		font-size: 11px;
		font-weight: 700;
		border-radius: 6px;
		padding: 3px 8px;
		background: #eef2f7;
		color: var(--ink2);
	}
	.rs-root .rs-badge[data-tone="warn"] {
		background: #fef3c7;
		color: #92400e;
	}
	.rs-root .rs-badge[data-tone="error"] {
		background: #fee2e2;
		color: #b91c1c;
	}
	.rs-root .rs-badge[data-tone="blue"] {
		background: #eef4ff;
		color: #1d4ed8;
	}
	.rs-root .rs-resend {
		margin-top: 8px;
		background: none;
		border: 0;
		padding: 0;
		font: inherit;
		font-size: 13px;
		font-weight: 700;
		color: var(--blue);
		cursor: pointer;
	}
	.rs-root .rs-resend:hover {
		color: #1d4fd7;
	}
	.rs-root .rs-resend:disabled {
		color: var(--ink4);
		cursor: default;
	}
	.rs-root .rs-pill {
		height: 40px;
		padding: 0 16px;
		border-radius: 10px;
		background: #eef1f4;
		border: 0;
		font-size: 14px;
		font-weight: 600;
		color: var(--ink);
		display: inline-flex;
		align-items: center;
		gap: 9px;
		cursor: pointer;
		white-space: nowrap;
		flex-shrink: 0;
	}
	.rs-root .rs-pill:hover {
		background: #e4e8ee;
	}
	.rs-root .rs-pill.dis {
		color: var(--ink4);
		cursor: default;
	}
	.rs-root .rs-pill.dis:hover {
		background: #eef1f4;
	}
	.rs-root .rs-pill svg {
		width: 12px;
		height: 12px;
	}
	.rs-root .rs-pagelabel {
		text-align: center;
		font-size: 12px;
		color: var(--ink3);
		margin-top: 4px;
	}
	/* role menu */
	.rs-root .rs-menucatch {
		position: fixed;
		inset: 0;
		z-index: 50;
	}
	.rs-root .r-menu {
		position: absolute;
		top: 100%;
		right: 16px;
		left: auto;
		z-index: 60;
		min-width: 220px;
		background: #fff;
		border: 1px solid var(--line2);
		border-radius: 12px;
		box-shadow: 0 12px 30px -10px rgba(15, 23, 42, 0.25);
		padding: 6px;
	}
	/* LSP-19142: opened for a row whose pill doesn't have ROLE_MENU_ESTIMATED_HEIGHT of room below it
	   (see shouldOpenRoleMenuUpward) — anchor to the pill's TOP edge instead of its bottom so the menu
	   never has to spill past the dialog/viewport on a short screen. */
	.rs-root .r-menu.menu-up {
		top: auto;
		bottom: 100%;
	}
	.rs-root .r-menu .opt {
		display: flex;
		align-items: center;
		gap: 8px;
		width: 100%;
		min-height: 38px;
		padding: 0 10px;
		font-size: 14px;
		color: var(--ink);
		border-radius: 8px;
		cursor: pointer;
		border: 0;
		background: transparent;
		text-align: left;
	}
	.rs-root .r-menu .opt:hover {
		background: #f3f4f6;
	}
	.rs-root .r-menu .opt.danger {
		color: #b91c1c;
	}
	.rs-root .r-menu .opt .check {
		width: 14px;
		height: 14px;
		color: var(--blue);
		opacity: 0;
		flex-shrink: 0;
	}
	.rs-root .r-menu .opt[data-selected="true"] .check {
		opacity: 1;
	}
	.rs-root .r-menu .opt[data-selected="true"] {
		color: var(--blue);
		font-weight: 600;
	}
	.rs-root .r-menu .note {
		font-size: 12px;
		color: var(--ink3);
		line-height: 1.5;
		padding: 8px 10px;
		max-width: 250px;
	}
	/* Create-contact form styling now lives in the shared CreateCustomerDialog (LSP-17773
	   staging QA reuse) — the bespoke rs-cmodal/rs-cmcard rules were removed with the inline form. */
</style>
